// The map on a canvas, in the approved Slate & Fire treatment (Oct 11 2026: Art/UI Redesign/UI Refresh/Map, site version
// 12 — its reference/map.js is the visual reference; the PNGs are in public/art/map/slate-fire). Two regimes share one
// view (centre cell + CSS px per cell):
//   REGION (s ≥ 20): the mossy ground with painted trees, boulders, lakes and ravines where the game's own terrain noise
//     puts them (rendered into world-aligned tiles at a settled scale and reused, scaled, while a gesture runs), the faint
//     survey grid, a hand-painted castle per city in its size tier (Cell.level 1–4) with the relationship's roofs and cloth
//     and a half-size painted pennant on its tower, the beginner-protection / truce shields, nameplates. A barbarian (NPC)
//     city wears its tier's Log Stockade in its authored colours, with no pennant (Oct 12 2026). Sprites fade into coloured
//     diamonds across 35 → 22 px per cell. The selected city gets the broad ivory glow under its own sprite — the castles'
//     baked PNGs, each stockade's baked here from its own silhouette — no ring, no ground pad, no enlargement. When it is
//     another player's city, that player's other castles wear the same glow tinted to their own relationship colour
//     (Oct 12 2026). Only the All nameplates layer hangs nameplates: selecting a city adds none.
//   REALM (s < 20): the same ground simplified (texture and soft forest masses), a marker per city coloured by guild
//     (relationships first) — diamonds that become points far out — and GUILDS ON THE MAP isolation. The selected city's
//     marker has an ivory edge and glow; its owner's other cities, a halo in their marker's own colour.
// Marches are dashed lines from origin to destination (drawn here, in the base pass, with their end marks) and, on the army
// layer over the base canvas (drawArmyLayer, Oct 15 2026), a small painted squad walking at the march's current position
// — its banner crimson for an attack, blue for support, grey for a return; a pulsing ground glow, red under an attack and
// blue under support — or the
// arrowhead it was wherever a squad can't be (the realm, an unknown kind, art not here, or squads that would overlap:
// army.ts), with the countdown pill above. Placement (per sprite family: castle or stockade), pennant mounts, the glow's
// frame and hit targets come from slateFire.ts, so drawing and hit testing agree.
import type { Cell } from "./chunks";
import { cellCentrePx, cellHash, visibleCells, wrap, wrapDelta, type View } from "./geo";
import { BARBARIAN_MARK, REL_COLORS, relationOf, type Relation, type Relations } from "./relations";
import type { TerrainCache } from "./terrain";
import type { Layers } from "./layers";
import {
  AURA_CORE_ALPHA, AURA_CORE_OFFSETS, AURA_FADE, AURA_LAYERS, CITY_RELATIONS, FINIAL_COLOR, FINIAL_OUTLINE, FLAG_SCALE, FOREST_COLOR,
  GLOW_PAD, GLOW_RES, GLOW_UNIT, GROUND_BASE, NPC_SHIELD_SCALE, POLE_COLOR, POLE_HIGHLIGHT, SELECT_IVORY, TEXTURE_CELLS, auraSize,
  castleAlpha, cityBox, clamp, familyOf, flagGeom, flagScreenRect, glowBox, hitCities, markerHalf, mix, paintedBottom, patchAlpha, pointAlpha,
  pointKind, pointMix, pointRadius, smooth, statusShield, textureAlpha, tierOf, treeAlpha,
  type CastleBox, type CityRelation, type HitItem, type SpriteFamily, type Tier,
} from "./slateFire";
import { art } from "@/kit/art";
import { accentRect, loadTerrainMaterials, materialDelta, noMaterials, type Material, type TerrainMaterials } from "./materials";
import { prepareCityDisplay } from "./cityColor";
import { RiverNetwork, type RiverSeg } from "./rivers";
import { RavineExtension } from "./ravineExtension";
import { findBridges, paintBridge, type Bridge } from "./bridges";
import { paintRivers, riverFieldAt, type PaintedRiver } from "./riverPaint";
import {
  REED_COUNT, ROCK_COUNT, TREE_COUNT, bridgeRect, castleUrl, imageAngle, loadPaintedWorld, pickBridge, pickShore, pickWall, sceneryRect,
  shoreMeta, wallMeta, paintedCastleBox, paintedFlagMount, paintedStatusMount, pennantAspect, type PaintedWorld,
} from "./paintedWorld";
import {
  ARMY_PAD, CrowdResolver, GLOW_COLORS, armySize, bannerFor, facing, footprint, frameAt, glowAt, glowFor, mirrored, phaseOf, type Banner, type Box as ArmyBox, type Facing,
} from "./army";
import { ARMY, type ArmySet } from "./armyData";

export type { Layers } from "./layers";

/** Map label faces follow Settings › Display: Source Sans 3 unless Classic fonts is on, scaled by the text size. */
function mapType(): { caps: string; text: string; tz: number } {
  const root = typeof document !== "undefined" ? document.documentElement : null;
  const classic = !!root?.classList.contains("classic-fonts");
  const tz = Number(root?.style.getPropertyValue("--tz")) || 1;
  return classic
    ? { caps: '"Cinzel", serif', text: '"EB Garamond", serif', tz }
    : { caps: '"Source Sans 3", system-ui, sans-serif', text: '"Source Sans 3", system-ui, sans-serif', tz };
}

export const REGION_MIN = 20;           // CSS px per cell: below this the realm (markers) is drawn
export const S_MIN = 0.35;              // the whole of a 1024 world on a phone
export const S_MAX = 110;
const TILE_CELLS = 8;                   // region ground tiles are 8 x 8 cells (a multiple of the 4-cell texture)
const TREE_NAMES = ["pines3", "pines3b", "oaks_mixed", "firs"] as const;
/** Scenery stays subordinate to the cities: scale its painted size about the same fixed foot positions. */
const SCENERY_SCALE = 0.75;

export interface MapAssets {
  /** the mossy ground: a seamless 768-px tile spanning 4 cells */
  terrain: HTMLImageElement;
  /** the painted tree clusters, in the reference's order: pines3, pines3b, oaks_mixed, firs */
  trees: HTMLImageElement[];
  boulders: HTMLImageElement;
  /** painted pennant cloth per relationship */
  flags: Record<CityRelation, HTMLImageElement | HTMLCanvasElement>;
  /** null when the file couldn't load: the old drawn badge stands in */
  shieldProtection: HTMLImageElement | null;
  shieldTruce: HTMLImageElement | null;
  /** The natural lake / ravine materials and accents (Oct 14 2026, materials.ts): each null until loaded or when its file
   *  failed (the procedural look stands in). Filled in place when they settle. */
  materials?: TerrainMaterials;
  /** Painted world v2 (Oct 15 2026, paintedWorld.ts): null until it has loaded, or when it is switched off (the e2e
   *  checks of the Slate & Fire family set window.__VALOR_MAP_ART__ = "slate-fire") or failed — the map then draws the
   *  Slate & Fire castles and its earlier terrain. Arrives with the materials (one ground repaint if late). */
  painted?: PaintedWorld | null;
  /** resolves when every material has loaded or failed; `materialsDone` once it has */
  materialsSettled?: Promise<void>;
  materialsDone?: boolean;
}

/** How long the first map paint waits for the terrain materials before drawing without them (they then arrive with one
 *  ground repaint). */
const MATERIALS_WAIT_MS = 2500;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error(`image failed: ${src}`));
    im.src = src;
  });
}

/** What every view needs before the first frame: the ground, the scenery, the pennants and the shields. The castles, the
 *  NPC stockades and the castles' glows (24 + 4 + 4 PNGs) are fetched as the map first shows them (MapRenderer's sprite
 *  store). */
export async function loadMapAssets(): Promise<MapAssets> {
  const sf = art.map.slateFire;
  const optional = (src: string) => loadImage(src).catch(() => null);
  const [terrain, trees, boulders, flags, shieldProtection, shieldTruce] = await Promise.all([
    loadImage(sf.terrain),
    Promise.all(TREE_NAMES.map((n) => loadImage(sf.scenery[n]))),
    loadImage(sf.scenery.boulders),
    Promise.all(CITY_RELATIONS.map((r) => loadImage(sf.flag(r)))),
    optional(sf.shieldProtection),
    optional(sf.shieldTruce),
  ]);
  // the natural terrain materials (optional): wait a little for them so the first paint has them; otherwise they arrive
  // later and the renderer repaints the ground once
  const materials = noMaterials();
  const out: Pick<MapAssets, "materials" | "materialsSettled" | "materialsDone" | "painted"> = { materials, materialsDone: false, painted: null };
  const paintedOn = typeof window === "undefined" || (window as unknown as { __VALOR_MAP_ART__?: string }).__VALOR_MAP_ART__ !== "slate-fire";
  out.materialsSettled = Promise.all([
    loadTerrainMaterials().then((m) => { Object.assign(materials, m); }).catch(() => undefined),
    paintedOn ? loadPaintedWorld().then((p) => { out.painted = p; }).catch(() => undefined) : Promise.resolve(),
  ])
    .then(() => undefined)
    .finally(() => { out.materialsDone = true; });
  await Promise.race([out.materialsSettled, new Promise((r) => setTimeout(r, MATERIALS_WAIT_MS))]);
  try {
    await Promise.all([document.fonts.load('600 12px "Cinzel"'), document.fonts.load('500 12px "EB Garamond"'), document.fonts.load('600 12px "Source Sans 3"')]);
  } catch {
    /* fonts are optional */
  }
  return {
    terrain,
    trees,
    boulders,
    flags: Object.fromEntries(CITY_RELATIONS.map((r, i) => [r, prepareCityDisplay(flags[i]) ?? flags[i]])) as MapAssets["flags"],
    shieldProtection,
    shieldTruce,
    get materials() { return out.materials; },
    get painted() { return out.painted; },
    get materialsSettled() { return out.materialsSettled; },
    get materialsDone() { return out.materialsDone; },
  };
}

export interface March {
  from: [number, number];
  to: [number, number];
  startMs: number;
  endMs: number;
  kind: "attack" | "support" | "return" | "other";
  incoming: boolean;
  /** stable id ("in:123" / "out:123") so a tapped march can be brought to the front */
  id?: string;
  count?: number;
}

/** Valor Tools (Oct 15 2026): a guild mark on a city — claimed (by you or a guildmate) or a target in a guild op. */
export interface GuildMark {
  x: number;
  y: number;
  kind: "claim" | "mine" | "op";
}

export interface NamePlate {
  name: string;
  sub?: string;
  own?: boolean;
}

export interface Scene {
  size: number;
  cities: Map<string, Cell>;
  cityList: Cell[];
  terrain: TerrainCache | null;
  relations: Relations;
  /** marker colour for the realm view */
  realmColor: (cell: Cell) => string;
  /** realm: dim every guild but this one */
  isolateGid: number | null;
  selected: [number, number] | null;
  /** When the selected city is another player's (not yours, not a barbarian's): that player's other cities ("x,y"), lit
   *  in their own relationship colours. */
  ownerCities?: ReadonlySet<string> | null;
  home: [number, number] | null;
  names: Map<string, NamePlate>;
  marches: March[];
  /** the march the player last tapped: drawn above every other march */
  frontMarch?: string | null;
  now: number;
  layers: Layers;
  /** changes whenever the set of cities changes (ground tiles keep trees off city cells) */
  cityVersion: number;
  /** Valor Tools' guild claims and op targets (only for players it's switched on for; drawn when layers.guildMarks) */
  guildMarks?: GuildMark[];
}

const key = (x: number, y: number) => `${x},${y}`;

/** Time left as the game prints it: H:MM:SS. */
export function fmtLeft(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

// ------------------------------------------------------------------------------------------------ sprites on demand

interface AlphaMask { w: number; h: number; a: Uint8Array }
type Slot = { state: "loading" } | { state: "ready"; img: HTMLImageElement; display: HTMLCanvasElement | null } | { state: "failed" };
/** a mask texel this opaque counts as the sprite (its soft grass fringe doesn't) */
const MASK_SOLID = 96;
const MASK_STEP = 4;
/** What a sprite PNG is, for the caches made from it: its family and tier. The castles of a tier share one silhouette —
 *  the base and all six relationship variants have identical alpha (checked in tests/unit/slateFire.test.ts) — so one mask
 *  serves them; a stockade's is its own. Keyed `${family}:${tier}`, never by tier alone. */
interface SpriteKind {
  family: SpriteFamily;
  tier: Tier;
  /** a painted world v2 castle: its own key (`painted:<relation>:<tier>` — its silhouette differs per relationship) */
  key?: string;
  /** false: no display boost (the painted world v2 castles are painted bright already) */
  boost?: boolean;
}
const kindKey = (k: SpriteKind) => k.key ?? `${k.family}:${k.tier}`;

/** Runs `cb` when the page is idle — requestIdleCallback where there is one, at most 300 ms later; else a short timer — and
 *  returns its cancel. A stockade's glow bake is one long task: in idle time it doesn't hold up input, a route change or a
 *  frame that is already waiting. */
function whenIdle(cb: () => void): () => void {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(() => cb(), { timeout: 300 });
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(cb, 16);
  return () => clearTimeout(id);
}

/** A tinted glow is kept at 1 px per logical px — half the ivory PNG's 2x. It is a broad blur, drawn ~65 CSS px wide at the
 *  opening zoom and ~200 at the closest (a citadel), so the PNG's extra pixels would add nothing visible for four times the
 *  memory. */
const TINT_RES = 1;
/** Tinted glows kept at most, a tier and colour each (≈ 1 MB apiece); the least recently drawn goes first. One owner's
 *  cities need four at most: one colour, four tiers. */
const TINT_KEEP = 8;

/** A castle glow tinted to a relationship colour, and the ivory PNG's own pixel size, which places it (glowBox) exactly as
 *  the ivory glow is placed. */
interface TintedGlow { canvas: HTMLCanvasElement; w: number; h: number }

/** The castles, the NPC stockades and the castles' glows, fetched the first time a frame needs them and kept for the
 *  session. What is made from them is made once as its PNG arrives, never in the drawing loop: each family's alpha mask
 *  per tier for hit testing (in the load callback: a quarter-size draw), and each stockade's selection glow — three broad
 *  blurs, tens of ms each — baked right after, one stockade per idle slot, so stockades arriving together don't hold the
 *  page up in one go (a glow a selected stockade is waiting for goes first). The castle glows tinted for an owner's other
 *  cities are made when first wanted, in an idle slot too, and only a few are kept. */
class SpriteStore {
  private slots = new Map<string, Slot>();
  private masks = new Map<string, AlphaMask>();
  private auras = new Map<string, HTMLCanvasElement>();
  /** stockade glows waiting for their bake, in order, and the cancel of the next bake's slot */
  private bakeQueue: { key: string; im: HTMLImageElement }[] = [];
  private cancelBake: (() => void) | null = null;
  /** tinted castle glows by `${tier}:${colour}`, the least recently drawn first; those waiting, and their slot's cancel */
  private tints = new Map<string, TintedGlow>();
  private tintQueue: { key: string; im: HTMLImageElement | HTMLCanvasElement; color: string }[] = [];
  private cancelTint: (() => void) | null = null;
  /** tints that couldn't be made (no canvas to be had — iOS Safari over its canvas memory): not tried again */
  private tintFailed = new Set<string>();
  private disposed = false;
  /** derived caches made so far (masks, glows) */
  builds = 0;
  /** sprites the current frame asked for that haven't arrived */
  pending = 0;

  constructor(private onSettle: () => void) {}

  /** The image if it has arrived; otherwise starts (or keeps waiting for) the fetch and returns null. A sprite's `kind` has
   *  its family's mask made for the tier when it arrives (if that family and tier have none yet), and with `aura` its
   *  selection glow queued for baking. */
  get(url: string, kind?: SpriteKind, aura = false): HTMLImageElement | null {
    const slot = this.slots.get(url);
    if (slot) {
      if (slot.state === "ready") return slot.img;
      if (slot.state === "loading") this.pending++;
      return null;
    }
    this.slots.set(url, { state: "loading" });
    this.pending++;
    const im = new Image();
    im.decoding = "async";
    const ready = () => {
      if (this.disposed) return;
      this.slots.set(url, { state: "ready", img: im, display: kind?.family === "castle" && kind.boost !== false ? prepareCityDisplay(im) : null });
      if (kind) {
        const k = kindKey(kind);
        if (!this.masks.has(k)) this.buildMask(k, im);
        if (aura) this.queueAura(k, im);
      }
      this.onSettle();
    };
    // Loaded is what counts: decode() first where it helps (the first draw doesn't stall), but a decode() rejection after a
    // good load (WebKit can reject under memory pressure) still uses the image. Only a load error gives up on it.
    im.onload = () => {
      const decoded = typeof im.decode === "function" ? im.decode() : Promise.resolve();
      decoded.then(ready, ready);
    };
    im.onerror = () => {
      if (this.disposed) return;
      this.slots.set(url, { state: "failed" });
      console.warn(`map art failed to load: ${url}`);
      this.onSettle();
    };
    im.src = url;
    return null;
  }

  /** Whether the image has arrived (asks for nothing: no fetch, not pending). */
  ready(url: string): boolean {
    return this.slots.get(url)?.state === "ready";
  }

  /** Whether the image failed to load (a painted castle that failed falls back to its Slate & Fire castle). */
  failed(url: string): boolean {
    return this.slots.get(url)?.state === "failed";
  }

  /** Queue a selection glow for an image that has arrived (painted castles bake theirs only when first selected or lit:
   *  24 glows baked up front would hold ~100 MB of canvas). */
  wantAura(key: string, im: HTMLImageElement) {
    if (this.disposed) return;
    this.queueAura(key, im);
  }

  /** The brighter castle display, prepared once on load; the original remains the mask and glow source. */
  display(url: string): HTMLCanvasElement | null {
    const slot = this.slots.get(url);
    return slot?.state === "ready" ? slot.display : null;
  }

  /** Whether the family's PNG for the tier is opaque at normalised (u, v); null while no sprite of that family and tier has
   *  loaded (a castle's mask never answers for a stockade, nor the other way round). */
  opaque(family: SpriteFamily, tier: Tier, u: number, v: number, key?: string): boolean | null {
    const m = this.masks.get(key ?? kindKey({ family, tier }));
    if (!m) return null;
    const x = Math.min(m.w - 1, Math.max(0, Math.floor(u * m.w)));
    const y = Math.min(m.h - 1, Math.max(0, Math.floor(v * m.h)));
    return m.a[y * m.w + x] >= MASK_SOLID;
  }

  /** The selection glow baked from this family and tier's own silhouette; null until it is. One a frame wants that is
   *  still waiting for its bake moves to the front of the queue, and the frame counts it as pending, as it would a sprite
   *  on its way (it draws without it meanwhile). */
  aura(family: SpriteFamily, tier: Tier, key?: string): HTMLCanvasElement | null {
    const k = key ?? kindKey({ family, tier });
    const c = this.auras.get(k);
    if (c) return c;
    const i = this.bakeQueue.findIndex((q) => q.key === k);
    if (i >= 0) {
      if (i > 0) this.bakeQueue.unshift(...this.bakeQueue.splice(i, 1));
      this.pending++;
    }
    return null;
  }

  /** A tier's castle glow in another colour — for the selected city's owner's other castles — or null until it is made.
   *  The ivory PNG is fetched as for a selection; the copy is tinted in an idle slot (every one waiting in the same slot:
   *  each is a draw and a fill, a millisecond or two), never in the drawing loop. Meanwhile the frame counts it as pending,
   *  as it would a sprite on its way, and draws the castle without it. */
  tinted(url: string, tier: Tier, color: string): TintedGlow | null {
    const k = `${tier}:${color}`;
    const t = this.tints.get(k);
    if (t) {
      this.tints.delete(k); // most recently drawn last
      this.tints.set(k, t);
      return t;
    }
    if (this.tintFailed.has(k)) return null; // drawn without it, and no redraw waiting on it
    const im = this.get(url);
    if (!im) return null;
    return this.queueTint(k, im, color);
  }

  /** The same for a glow baked here (a painted castle's): `key` names the glow, `src` is it (null while it bakes). */
  tintedCanvas(key: string, src: HTMLCanvasElement | null, color: string): TintedGlow | null {
    const k = `${key}:${color}`;
    const t = this.tints.get(k);
    if (t) {
      this.tints.delete(k);
      this.tints.set(k, t);
      return t;
    }
    if (this.tintFailed.has(k)) return null;
    if (!src) {
      this.pending++;
      return null;
    }
    return this.queueTint(k, src, color);
  }

  private queueTint(k: string, im: HTMLImageElement | HTMLCanvasElement, color: string): null {
    if (!this.tintQueue.some((q) => q.key === k)) this.tintQueue.push({ key: k, im, color });
    this.pending++;
    this.scheduleTint();
    return null;
  }

  /** The caches made so far, by key (the e2e checks read them). */
  keys(): { masks: string[]; auras: string[]; tints: string[] } {
    return { masks: [...this.masks.keys()].sort(), auras: [...this.auras.keys()].sort(), tints: [...this.tints.keys()].sort() };
  }

  /** The baked glows themselves, by key (the e2e checks look at their pixels, and that teardown releases them). */
  auraCanvases(): { key: string; canvas: HTMLCanvasElement }[] {
    return [...this.auras].map(([key, canvas]) => ({ key, canvas }));
  }

  /** The tinted castle glows, by key (live canvases: the e2e checks read their colour, and that they are released). */
  tintCanvases(): { key: string; canvas: HTMLCanvasElement }[] {
    return [...this.tints].map(([key, t]) => ({ key, canvas: t.canvas }));
  }

  private buildMask(key: string, im: HTMLImageElement) {
    try {
      const w = Math.max(1, Math.ceil(im.naturalWidth / MASK_STEP));
      const h = Math.max(1, Math.ceil(im.naturalHeight / MASK_STEP));
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      const g = c.getContext("2d", { willReadFrequently: true });
      if (!g) return;
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = "high";
      g.drawImage(im, 0, 0, w, h);
      const d = g.getImageData(0, 0, w, h).data;
      const a = new Uint8Array(w * h);
      for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3];
      c.width = c.height = 0;
      this.masks.set(key, { w, h, a });
      this.builds++;
    } catch {
      /* no mask: hit testing falls back to the sprite's rect */
    }
  }

  private queueAura(key: string, im: HTMLImageElement) {
    if (this.auras.has(key) || this.bakeQueue.some((q) => q.key === key)) return;
    this.bakeQueue.push({ key, im });
    this.scheduleBake();
  }

  /** Bakes the next queued glow in an idle slot of its own, then lets the map redraw (a selected stockade gets its glow). */
  private scheduleBake() {
    if (this.cancelBake || !this.bakeQueue.length || this.disposed) return;
    this.cancelBake = whenIdle(() => {
      this.cancelBake = null;
      if (this.disposed) return;
      const next = this.bakeQueue.shift();
      if (next && !this.auras.has(next.key)) this.bakeAura(next.key, next.im);
      this.onSettle();
      this.scheduleBake();
    });
  }

  private bakeAura(key: string, im: HTMLImageElement) {
    try {
      const c = bakeSelectionAura(im);
      if (!c) return;
      this.auras.set(key, c);
      this.builds++;
    } catch {
      /* no glow: the selected stockade is drawn without one */
    }
  }

  /** Tints every glow waiting, in one idle slot, then lets the map redraw. */
  private scheduleTint() {
    if (this.cancelTint || !this.tintQueue.length || this.disposed) return;
    this.cancelTint = whenIdle(() => {
      this.cancelTint = null;
      if (this.disposed) return;
      const queue = this.tintQueue;
      this.tintQueue = [];
      for (const q of queue) if (!this.tints.has(q.key)) this.bakeTint(q.key, q.im, q.color);
      this.onSettle();
    });
  }

  /** The ivory glow's own alpha in the city's colour, at TINT_RES; then the least recently drawn beyond TINT_KEEP go. */
  private bakeTint(key: string, im: HTMLImageElement | HTMLCanvasElement, color: string) {
    const c = document.createElement("canvas");
    c.dataset.tint = key; // which glow this is (the e2e checks refuse these canvases to try the failure path)
    const iw = im instanceof HTMLImageElement ? im.naturalWidth : im.width;
    const ih = im instanceof HTMLImageElement ? im.naturalHeight : im.height;
    try {
      c.width = Math.max(1, Math.round((iw * TINT_RES) / GLOW_RES));
      c.height = Math.max(1, Math.round((ih * TINT_RES) / GLOW_RES));
      const g = c.getContext("2d");
      if (!g) throw new Error("no 2d context");
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = "high";
      g.drawImage(im, 0, 0, c.width, c.height);
      g.globalCompositeOperation = "source-in";
      g.fillStyle = color;
      g.fillRect(0, 0, c.width, c.height);
    } catch {
      // no tint: the castle is drawn without its glow, and this one isn't tried again (a retry each frame would redraw the
      // map on every idle slot and keep the frame pending)
      c.width = c.height = 0;
      if (!this.tintFailed.size) console.warn("map: a city glow couldn't be tinted (no canvas); the owner's cities go unlit");
      this.tintFailed.add(key);
      return;
    }
    this.tints.set(key, { canvas: c, w: iw, h: ih });
    this.builds++;
    for (const [k, t] of this.tints) {
      if (this.tints.size <= TINT_KEEP) break;
      t.canvas.width = t.canvas.height = 0;
      this.tints.delete(k);
    }
  }

  dispose() {
    this.disposed = true;
    this.cancelBake?.();
    this.cancelBake = null;
    this.bakeQueue = [];
    this.cancelTint?.();
    this.cancelTint = null;
    this.tintQueue = [];
    for (const slot of this.slots.values()) {
      if (slot.state === "ready" && slot.display) slot.display.width = slot.display.height = 0;
    }
    this.slots.clear();
    this.masks.clear();
    // let the browser free the glows' bitmaps now (iOS Safari caps total canvas memory)
    for (const c of this.auras.values()) c.width = c.height = 0;
    this.auras.clear();
    for (const t of this.tints.values()) t.canvas.width = t.canvas.height = 0;
    this.tints.clear();
    this.tintFailed.clear();
  }
}

/** The reference's castleSelectionAura (map.js), step for step, baked once per stockade tier from its PNG's own alpha: the
 *  silhouette in ivory, fading out over its lower part (its footprint); drawn 220 logical px wide with 135 px padding at 2x
 *  (so the glow's frame is the castles' glow frame: slateFire glowBox) — three broad blurs, then a thin core from six small
 *  offsets — and the unblurred image cut out of the middle, so the sprite over it keeps its own colours as it fades. */
export function bakeSelectionAura(image: HTMLImageElement): HTMLCanvasElement | null {
  const iw = image.naturalWidth;
  const ih = image.naturalHeight;
  if (!iw || !ih) return null;
  const mask = document.createElement("canvas");
  mask.width = iw;
  mask.height = ih;
  const m = mask.getContext("2d");
  if (!m) return null;
  m.drawImage(image, 0, 0);
  m.globalCompositeOperation = "source-in";
  const fade = m.createLinearGradient(0, 0, 0, ih);
  fade.addColorStop(0, SELECT_IVORY);
  fade.addColorStop(AURA_FADE[0], SELECT_IVORY);
  fade.addColorStop(AURA_FADE[1], "rgba(255,255,237,0)");
  m.fillStyle = fade;
  m.fillRect(0, 0, iw, ih);
  const unitW = GLOW_UNIT;
  const unitH = (GLOW_UNIT * ih) / iw;
  const [lw, lh] = auraSize(iw, ih);
  const out = document.createElement("canvas");
  out.width = lw * GLOW_RES;
  out.height = lh * GLOW_RES;
  const g = out.getContext("2d");
  if (!g) {
    mask.width = mask.height = 0;
    return null;
  }
  g.setTransform(GLOW_RES, 0, 0, GLOW_RES, 0, 0);
  g.shadowColor = SELECT_IVORY;
  // shadowBlur is in device px (the transform doesn't scale it): the logical blurs × GLOW_RES; offsets are logical
  for (const [blur, alpha] of AURA_LAYERS) {
    g.shadowBlur = blur * GLOW_RES;
    g.globalAlpha = alpha;
    g.drawImage(mask, GLOW_PAD, GLOW_PAD, unitW, unitH);
  }
  g.shadowBlur = 0;
  g.globalAlpha = AURA_CORE_ALPHA;
  for (const [dx, dy] of AURA_CORE_OFFSETS) g.drawImage(mask, GLOW_PAD + dx, GLOW_PAD + dy, unitW, unitH);
  g.globalCompositeOperation = "destination-out";
  g.globalAlpha = 1;
  g.drawImage(image, GLOW_PAD, GLOW_PAD, unitW, unitH);
  mask.width = mask.height = 0;
  return out;
}

// ------------------------------------------------------------------------------------------------ ground helpers

/** Gentle value noise for the ground's light/dark mottling (the reference's field(x, y, 34)), periodic over the world so
 *  it wraps without a seam. Decoration only: the game's terrain is TerrainCache's. */
function groundField(x: number, y: number, size: number): number {
  const n = Math.max(1, Math.round(size / 34));
  const p = size / n;
  const fx = x / p;
  const fy = y / p;
  const bx = Math.floor(fx);
  const by = Math.floor(fy);
  const tx = smooth(0, 1, fx - bx);
  const ty = smooth(0, 1, fy - by);
  const hh = (i: number, j: number) => cellHash(((i % n) + n) % n, ((j % n) + n) % n, 531);
  return mix(mix(hh(bx, by), hh(bx + 1, by), tx), mix(hh(bx, by + 1), hh(bx + 1, by + 1), tx), ty);
}

/** Realm tiles grow with distance so there are always a few dozen at most: the smallest 8·2ⁿ cells at least 160 px wide. */
function realmTileCells(s: number, size: number): number {
  let r = TILE_CELLS;
  while (r * s < 160 && r * 2 <= size) r *= 2;
  return r;
}

// ------------------------------------------------------------------------------------------------ the renderer

interface GroundTile { canvas: HTMLCanvasElement; dpr: number }
/** Ground tiles kept at most: by count, and by pixels (a close-in tile on a 3x phone is 2640 x 2640 px, ~28 MB). */
const TILE_KEEP = 96;
const TILE_KEEP_AFTER = 64;
const TILE_PIXEL_BUDGET = 40e6;
/** Ground tiles are rendered at no more than 2 device px per CSS px: the ground texture carries ~1.75 px of detail per CSS
 *  px even at the closest zoom (768 px over 4 cells of 110), so a 3x tile adds nothing visible but costs 2.25 times the
 *  pixels to paint and keep. The castles, pennants and glow are drawn straight onto the map at the full ratio. */
const TILE_DPR_MAX = 2;
/** Below this the fading trees, boulders, ravines and mottling are invisible: they aren't drawn. */
const MIN_ALPHA = 0.02;
/** From this many px per cell (the region views) the lakes are drawn from every cell; farther out, from the terrain sampled
 *  every P/2 cells (the forest field's own samples). */
const LAKE_CELLS_MIN = 12;

/** A city as one frame draws it (north to south, west to east: later = in front). */
interface CityItem {
  c: Cell;
  tier: Tier;
  rel: Relation;
  /** castle, or an NPC's stockade (from rel): its PNG, rect, mask, glow, nameplate and shield anchors */
  family: SpriteFamily;
  px: number;
  py: number;
  box: CastleBox;
  selected: boolean;
  /** one of the selected city's owner's other cities (a player's castle): its colour, the roofs' and pennant's */
  lit: string | null;
  /** the painted world v2 castle (Oct 15 2026) or the Slate & Fire art (an NPC's stockade; every castle when the painted
   *  world is off, and any painted castle whose image failed) */
  painted: boolean;
  /** the image it wears */
  url: string;
  /** its hit mask / glow key: `painted:<relation>:<tier>`, or the family's per tier */
  key: string;
}

/** The PNG a city wears: an NPC its tier's Log Stockade, anyone else the castle of its relationship and tier. */
function spriteUrl(it: CityItem): string {
  return it.url;
}

interface DrawnCastle {
  url: string;
  flag: { x: number; y: number; w: number; h: number } | null;
  /** the selected city's ivory glow */
  glow: { x: number; y: number; w: number; h: number } | null;
  /** an owner's other city's tinted glow */
  litGlow: { x: number; y: number; w: number; h: number } | null;
  shield: { x: number; y: number; h: number } | null;
}

/** The last frame, for the e2e checks (only kept when `debug` is on). */
export interface FrameMarch {
  id: string | undefined;
  ax: number;
  ay: number;
  rep: "squad" | "arrow";
  mirror: boolean;
  banner: Banner | null;
  /** the squad's drawn box (CSS px; null for an arrow) and the countdown pill's */
  box: ArmyBox | null;
  pill: ArmyBox | null;
  /** the walking frame drawn (0–7; 0 for an arrow, and held at 0 under reduced motion) */
  walk: number;
}

export interface MapFrame {
  s: number;
  regime: "region" | "realm";
  castleAlpha: number;
  cities: { x: number; y: number; rel: Relation; family: SpriteFamily; tier: Tier; box: { x: number; y: number; w: number; h: number }; sprite: string | null; flag: { x: number; y: number; w: number; h: number } | null; glow: { x: number; y: number; w: number; h: number } | null; shield: { x: number; y: number; h: number } | null; marker: boolean }[];
  /** each march's marker — where a tap brings it to the front — and, from the army layer's last frame, how it was drawn:
   *  a walking squad (its banner, facing and drawn box) or an arrow; and the march drawn on top */
  marches: FrameMarch[];
  /** the nameplates drawn, by city name */
  plates: string[];
  /** and where (CSS px) */
  plateBoxes: { name: string; x: number; y: number; w: number; h: number }[];
  /** the selected city's owner's other cities drawn lit: the colour, the tinted glow under the castle (where its castle is
   *  drawn) and whether its marker (with its halo) is drawn */
  lit: { x: number; y: number; color: string; glow: { x: number; y: number; w: number; h: number } | null; marker: boolean }[];
  frontMarch: string | null;
  selected: [number, number] | null;
  pending: number;
  ms: number;
  /** the army layer's last frame: its cost, whether any squad is walking, and the marches waiting to get their squads back */
  armyMs: number;
  squads: number;
  restorePending: boolean;
}

export class MapRenderer {
  private ground = new Map<string, GroundTile>();
  private groundS = 0;
  private groundR = TILE_CELLS;
  private groundRegion = true;
  private groundVersion = -1;
  private groundTerrain: TerrainCache | null = null;
  private groundSize = 0;
  private lastUse = new Map<string, number>();
  private frame = 0;
  private sprites: SpriteStore;
  private texAvg: string | null = null;
  private texAvgOf: CanvasImageSource | null = null;
  private fields = new Map<number, DensityField>();
  private fieldsFor: TerrainCache | null = null;
  private riverNetwork: RiverNetwork | null = null;
  private ravineExt: RavineExtension | null = null;
  private extFor: TerrainCache | null = null;
  private extVersion = -1;
  private riversFor: TerrainCache | null = null;
  private riversVersion = -1;
  private waterwayScene: Scene | null = null;
  private sortedFor: Cell[] | null = null;
  private sorted: Cell[] = [];
  private frameInfo: MapFrame["cities"] | null = null;
  private framePlates: string[] | null = null;
  private framePlateBoxes: MapFrame["plateBoxes"] | null = null;
  private frameLit: MapFrame["lit"] | null = null;
  /** the soft halos of an owner's lit markers, one per colour, made when first wanted (one that can't be made stays null) */
  private halos = new Map<string, HTMLCanvasElement | null>();
  /** set by the screen: a sprite arrived (or failed), so the next frame should draw */
  onChange: (() => void) | null = null;
  /** keep a summary of each frame in lastFrame (the e2e checks switch it on) */
  debug = false;
  lastFrame: MapFrame | null = null;
  /** ground tiles rendered so far */
  tileRenders = 0;
  /** base-canvas repaints so far (the e2e checks hold the base to its idle cadence while the armies walk) */
  baseDraws = 0;
  /** army-layer repaints so far */
  armyDraws = 0;
  /** the army layer's last frame: what it drew, for hit testing and the loop's cadence (null before its first frame) */
  private armyFrame: ArmyFrame | null = null;
  private crowd = new CrowdResolver();
  private facings = new Map<string, Facing>();
  private glowTex = new Map<string, HTMLCanvasElement>();
  private armyFailedWarned = false;
  /** sprites the last frame wanted that hadn't arrived (the screen keeps its loader up until they do) */
  pendingSprites = 0;

  constructor(public assets: MapAssets) {
    this.sprites = new SpriteStore(() => this.onChange?.());
    // materials still loading at the first paint: repaint the ground once when they settle
    if (assets.materialsSettled && !assets.materialsDone) {
      void assets.materialsSettled.then(() => {
        if (this.disposed) return;
        this.invalidateGround();
        this.materialRepaints++;
        this.onChange?.();
      });
    }
  }

  private disposed = false;
  /** ground repaints for late-arriving materials (at most one; e2e checks) */
  materialRepaints = 0;

  /** derived sprite caches built so far (each family's hit mask per tier, each stockade tier's glow): made once each */
  get cacheBuilds() {
    return this.sprites.builds;
  }

  /** the derived caches by key — `castle:2`, `npc:2`… (e2e checks) */
  get cacheKeys() {
    return this.sprites.keys();
  }

  /** e2e checks: whether the hit mask the map uses for this family and tier is solid at normalised (u, v); null: none yet */
  maskAt(family: SpriteFamily, tier: Tier, u: number, v: number, key?: string): boolean | null {
    return this.sprites.opaque(family, tier, u, v, key);
  }

  /** e2e checks: the baked stockade glows (live canvases) */
  get auraCanvases() {
    return this.sprites.auraCanvases();
  }

  /** e2e checks: the tinted castle glows of an owner's other cities (live canvases) */
  get tintCanvases() {
    return this.sprites.tintCanvases();
  }

  dispose() {
    this.disposed = true;
    this.sprites.dispose(); // the army atlases go with the castles
    this.crowd.reset();
    this.facings.clear();
    this.armyFrame = null;
    for (const c of this.glowTex.values()) c.width = c.height = 0;
    this.glowTex.clear();
    this.invalidateGround();
    this.fields.clear();
    this.riverNetwork = null;
    this.riversFor = null;
    this.ravineExt = null;
    this.extFor = null;
    this.waterwayScene = null;
    for (const c of this.halos.values()) if (c) c.width = c.height = 0;
    this.halos.clear();
    this.onChange = null;
  }

  private halo(color: string): HTMLCanvasElement | null {
    if (!this.halos.has(color)) this.halos.set(color, makeHalo(color));
    return this.halos.get(color)!;
  }

  /** Forget the ground tiles (after a zoom settles, or when the cities or layers change). */
  invalidateGround() {
    for (const k of [...this.ground.keys()]) this.dropTile(k);
    this.lastUse.clear();
  }

  /** The scale the ground tiles were rendered at; the caller re-renders them when a zoom gesture ends. */
  get groundScale() {
    return this.groundS;
  }

  draw(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number, view: View, scene: Scene, settled: boolean) {
    const t0 = performance.now();
    this.frame++;
    this.baseDraws++;
    this.waterwayScene = scene;
    this.sprites.pending = 0;
    this.frameInfo = this.debug ? [] : null;
    this.framePlates = this.debug ? [] : null;
    this.framePlateBoxes = this.debug ? [] : null;
    this.frameLit = this.debug ? [] : null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, w, h);
    const region = view.s >= REGION_MIN;
    this.drawGround(ctx, w, h, dpr, view, scene, settled, region);
    if (region) this.drawRegion(ctx, w, h, dpr, view, scene);
    else this.drawRealm(ctx, w, h, dpr, view, scene);
    if (scene.layers.guildMarks && scene.guildMarks?.length) this.drawGuildMarks(ctx, w, h, view, scene);
    if (scene.layers.marches) this.drawMarchLines(ctx, w, h, view, scene);
    this.evictGround();
    this.pendingSprites = this.sprites.pending;
    const ms = performance.now() - t0;
    if (this.debug) {
      // the markers are the army layer's; until it has drawn this view, where they will be
      const marches: FrameMarch[] = scene.layers.marches
        ? this.marchGeo(w, h, view, scene).map((g) => ({ id: g.m.id, ax: g.ax, ay: g.ay, rep: "arrow" as const, mirror: false, banner: null, box: null, pill: null, walk: 0 }))
        : [];
      this.lastFrame = {
        s: view.s, regime: region ? "region" : "realm", castleAlpha: region ? castleAlpha(view.s) : 0, cities: this.frameInfo ?? [],
        plates: this.framePlates ?? [], plateBoxes: this.framePlateBoxes ?? [], lit: this.frameLit ?? [], marches, frontMarch: scene.frontMarch ?? null, selected: scene.selected, pending: this.pendingSprites, ms,
        armyMs: 0, squads: 0, restorePending: false,
      };
    }
  }

  /** e2e checks: the last base frame with the army layer's last answers for the marches (when it has drawn the same view). */
  debugFrame(): MapFrame | null {
    const f = this.lastFrame;
    const a = this.armyFrame;
    if (!f || !a) return f;
    return { ...f, marches: a.items.map((it) => ({ id: it.id, ax: it.ax, ay: it.ay, rep: it.rep, mirror: it.mirror, banner: it.banner, box: it.box, pill: it.pill, walk: it.walk })), armyMs: a.ms, squads: a.squads, restorePending: a.pending };
  }

  /** The loop reads these after each army frame: whether any squad is walking (12 fps), and whether a crowded march is
   *  waiting out its clearance (keep drawing). */
  get armyWalking() {
    return (this.armyFrame?.squads ?? 0) > 0;
  }
  get armyRestorePending() {
    return this.armyFrame?.pending ?? false;
  }
  /** e2e checks: the view the army layer last drew (it must be the base's, mid-gesture too) */
  get armyView(): View | null {
    return this.armyFrame ? { ...this.armyFrame.view } : null;
  }
  /** e2e checks: the marches in arrow mode because of the crowd */
  get crowdArrows() {
    return this.crowd.inArrowMode;
  }

  // ---------------------------------------------------------------------------------------------- ground
  private drawGround(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number, view: View, scene: Scene, settled: boolean, region: boolean) {
    const s = view.s;
    const ratio = this.groundS ? s / this.groundS : 0;
    // tiles are rendered at a settled scale and reused, scaled, during a gesture — unless the zoom has moved so far that
    // they'd be too many and too large (zooming out past half) or too blurred (zooming in past 3x), or the regime, cities,
    // terrain or world changed
    if (
      this.groundS === 0 || ratio > 3 || ratio < 0.5 || (settled && this.groundS !== s) || region !== this.groundRegion ||
      scene.cityVersion !== this.groundVersion || scene.terrain !== this.groundTerrain || scene.size !== this.groundSize
    ) {
      this.invalidateGround();
      this.groundS = s;
      this.groundRegion = region;
      this.groundR = region ? TILE_CELLS : realmTileCells(s, scene.size);
      this.groundVersion = scene.cityVersion;
      this.groundTerrain = scene.terrain;
      this.groundSize = scene.size;
    }
    const R = this.groundR;
    const tdpr = Math.min(dpr, TILE_DPR_MAX);
    const vis = visibleCells(view, w, h, 0);
    const tx0 = Math.floor(vis.x0 / R);
    const tx1 = Math.floor(vis.x1 / R);
    const ty0 = Math.floor(vis.y0 / R);
    const ty1 = Math.floor(vis.y1 / R);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "medium";
    // tiles snap to device pixels so neighbours meet without a hairline
    const snapX = (cx: number) => Math.round((w / 2 + (cx - view.cx) * s) * dpr) / dpr;
    const snapY = (cy: number) => Math.round((h / 2 - (cy - view.cy) * s) * dpr) / dpr;
    for (let ty = ty0; ty <= ty1; ty++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        const tile = this.groundTile(tx, ty, R, tdpr, scene, region);
        // the tile covers cells x ∈ [tx·R, tx·R + R), y ∈ [ty·R, ty·R + R): its top-left on screen is the corner (tx·R, ty·R + R)
        const x0 = snapX(tx * R);
        const x1 = snapX(tx * R + R);
        const y0 = snapY(ty * R + R);
        const y1 = snapY(ty * R);
        ctx.drawImage(tile.canvas, x0, y0, x1 - x0, y1 - y0);
      }
    }
  }

  private groundTile(tx: number, ty: number, R: number, dpr: number, scene: Scene, region: boolean): GroundTile {
    const { size } = scene;
    // tiles that are the same piece of the wrapping world share a canvas (far out, the world repeats across the screen)
    const per = size % R === 0 ? size / R : 0;
    const k = per ? `${R}:${((tx % per) + per) % per},${((ty % per) + per) % per}` : `${R}:${tx},${ty}`;
    this.lastUse.set(k, this.frame);
    const hit = this.ground.get(k);
    if (hit && hit.dpr === dpr) return hit;
    const s = this.groundS;
    const d = R * s;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(d * dpr));
    canvas.height = canvas.width;
    const g = canvas.getContext("2d")!;
    g.setTransform(canvas.width / d, 0, 0, canvas.width / d, 0, 0);
    this.paintGround(g, tx, ty, R, s, scene, region);
    this.tileRenders++;
    const tile = { canvas, dpr };
    this.ground.set(k, tile);
    return tile;
  }

  /** One ground tile: the base colour and mossy texture (aligned to the world), the mottling, the survey grid, soft forest
   *  masses, and — in the region — the game's trees, boulders, lakes and ravines. */
  private paintGround(g: CanvasRenderingContext2D, tx: number, ty: number, R: number, s: number, scene: Scene, region: boolean) {
    const { size } = scene;
    const X0 = tx * R;
    const Y0 = ty * R;
    const top = Y0 + R; // the tile's north edge
    const d = R * s;
    // a cell's (unwrapped) north-west corner in this tile's px
    const cellPx = (x: number, y: number): [number, number] => [(x - X0) * s, (top - (y + 1)) * s];
    g.fillStyle = GROUND_BASE;
    g.fillRect(0, 0, d, d);
    // the 768-px texture spans 4 cells; tiles start on multiples of 4 cells, so every tile starts at the texture's origin.
    // Far out a repeat is only a few px wide and would shimmer into a dot grid, so it gives way to its average colour.
    // painted world v2: the painted meadow (its swatch mirrored into a 4-cell repeat, so it is seamless and wraps with
    // the world), fully opaque — it is the ground's colour now, not a texture over the slate base
    const meadow = this.assets.painted?.meadow ?? null;
    const tex = meadow ?? this.assets.terrain;
    const ta = meadow ? 1 : textureAlpha(s);
    const detail = smooth(6, 24, s * TEXTURE_CELLS);
    const pat = detail > 0 ? g.createPattern(tex, "repeat") : null;
    if (pat) {
      pat.setTransform(new DOMMatrix().scale((s * TEXTURE_CELLS) / tex.width));
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = "medium";
      g.globalAlpha = ta * detail;
      g.fillStyle = pat;
      g.fillRect(0, 0, d, d);
    }
    if (meadow && pat && detail > 0) {
      // the mirrored swatch repeats every 4 cells and its mirror axes would show as a lattice: a second pass of it at twice
      // the size, offset (an 8-cell period, so tiles and the world wrap still join), breaks it up — axis-aligned, as a
      // rotated pattern costs several times as much to raster on a phone's software path
      const pat2 = g.createPattern(meadow, "repeat");
      if (pat2) {
        pat2.setTransform(new DOMMatrix().translate(s * 2, s * 3).scale((s * TEXTURE_CELLS * 2) / meadow.width));
        g.globalAlpha = 0.42 * detail;
        g.fillStyle = pat2;
        g.fillRect(0, 0, d, d);
      }
    }
    if (detail < 1) {
      g.globalAlpha = ta * (1 - detail);
      g.fillStyle = this.textureAverage(tex);
      g.fillRect(0, 0, d, d);
    }
    g.globalAlpha = 1;
    // mottling: soft dry / lush variation in the ground (the reference's _drawGround), with the neighbours that lean into
    // this tile. Close in only, fading with the trees: farther out its broad dark circles would read as forests, and only
    // the game's terrain should (the masses below).
    const step = 2;
    const mottle = region ? treeAlpha(s) : 0;
    g.globalAlpha = mottle;
    for (let y = Math.floor((Y0 - step) / step) * step; mottle >= MIN_ALPHA && y < top + step; y += step) {
      for (let x = Math.floor((X0 - step) / step) * step; x < X0 + R + step; x += step) {
        const wx = wrap(x, size);
        const wy = wrap(y, size);
        const t = groundField(wx, wy, size);
        const n = cellHash(wx, wy, 24);
        const [ox, oy] = cellPx(x, y + step - 1);
        const z = step * s;
        g.fillStyle = t > 0.58 ? "rgba(101,99,66,.10)" : t < 0.37 ? "rgba(42,64,43,.14)" : "rgba(125,119,81,.04)";
        g.beginPath();
        g.ellipse(ox + z * 0.55, oy + z * 0.48, z * 0.8, z * 0.7, n * 3, 0, Math.PI * 2);
        g.fill();
      }
    }
    g.globalAlpha = 1;
    if (region && scene.layers.grid && s >= 16) {
      g.strokeStyle = `rgba(34,39,28,${smooth(16, 42, s) * 0.16})`;
      g.lineWidth = 1;
      g.beginPath();
      for (let i = 0; i <= R; i++) {
        const p = Math.round(i * s) + 0.5;
        g.moveTo(p, 0); g.lineTo(p, d);
        g.moveTo(0, p); g.lineTo(d, p);
      }
      g.stroke();
    }
    const terrain = scene.terrain;
    if (!terrain || !scene.layers.forest) return;
    // Simplified terrain from the game's own terrain, sampled per P-cell patch: the forests' soft masses (far out the
    // forest's shape, close in a faint shadow under the trees) and, far out, the lakes
    const P = s < 2 ? 16 : s < 6 ? 8 : 4;
    const field = this.densityField(terrain, P, size);
    // the forests' soft masses: one pixel per patch (the forest cover blurred with its neighbours), stretched over the
    // tile with smoothing, so the masses are continuous across patches and tiles — no lattice of blobs
    const nx = Math.ceil(R / P) + 2;
    // (Oct 14 2026: lighter, and only where the forest is dense — the map reads as open country with woods, not a forest)
    const massAlpha = patchAlpha(s) * (this.assets.painted?.meadow ? 0.35 : 0.6);
    const mass = document.createElement("canvas");
    mass.width = nx;
    mass.height = nx;
    const mg = mass.getContext("2d")!;
    const img = mg.createImageData(nx, nx);
    const [fr, fg, fb] = FOREST_COLOR;
    let any = false;
    for (let j = 0; j < nx; j++) {
      // row 0 is the patch row just north of the tile; column 0 the patch column just west of it
      const py = Math.round(top / P) - j;
      for (let i = 0; i < nx; i++) {
        const px = Math.round(X0 / P) - 1 + i;
        const k = (j * nx + i) * 4;
        img.data[k] = fr;
        img.data[k + 1] = fg;
        img.data[k + 2] = fb;
        const a = Math.round(255 * smooth(0.42, 0.92, field.forestSmooth(px, py)));
        img.data[k + 3] = a;
        if (a) any = true;
      }
    }
    if (any) {
      mg.putImageData(img, 0, 0);
      // a plain bilinear stretch is exactly the softness wanted (and far cheaper than the high-quality filter)
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = "low";
      g.globalAlpha = massAlpha;
      g.drawImage(mass, -P * s, -P * s, nx * P * s, nx * P * s);
      g.globalAlpha = 1;
    }
    // The lakes: each one continuous body of water in the same painted style at every zoom — cell by cell from
    // LAKE_CELLS_MIN px per cell (the region views; a city's own cell is never water, as before), farther out the same
    // painting over the terrain sampled every P/2 cells (the forest field's samples: cheap, and a lake keeps its size and
    // colours, only coarser, instead of swelling into a patch-sized blob), fading out where a sample is only a few pixels
    // (the whole-world view shows no lakes, as the reference's realm shows none)
    const lakeAt = (gx: number, gy: number) => {
      const wx = wrap(gx, size);
      const wy = wrap(gy, size);
      return terrain.at(wx, wy) === 3 && !scene.cities.has(key(wx, wy)) ? 1 : 0;
    };
    // a castle's footprint stays dry ground: the shore bends round it (Oct 14 2026 — castles stood in the shallows)
    const castleAt = (gx: number, gy: number) => {
      const c = scene.cities.get(key(wrap(gx, size), wrap(gy, size)));
      return !!c && !!tierOf(c.level);
    };
    const lakeWater = (x: number, y: number) => lakeFieldAt(x, y, size, lakeAt, castleAt);
    // the terrain as painted: the game's, with painted world v2's cosmetic gorge extensions (ravineExtension.ts)
    const code = this.terrainCode(scene);
    // Streams follow fixed world routes; detailed ground bakes them just once, like the lake and gorge fields.
    const streamAlpha = smooth(12, 28, s);
    const waterways = streamAlpha >= MIN_ALPHA
      ? this.waterwayGeometry(X0 - 2, Y0 - 2, X0 + R + 2, top + 2, scene, region)
      : { rivers: [] as RiverSeg[], bridges: [] as Bridge[] };
    const pwm = this.assets.painted?.materials;
    const pwLake: PaintedLake | null = pwm?.lakeWater && pwm.riverWater && pwm.shoreGravel
      ? { water: pwm.lakeWater, shallows: pwm.riverWater, gravel: pwm.shoreGravel, detail: paintedLakeDetail(s) } : null;
    if (s >= LAKE_CELLS_MIN) {
      paintLakes(g, X0, top, R, 1, s, size, 1, lakeAt, castleAt, this.assets.materials, pwLake);
    } else {
      paintLakes(g, X0, top, R, P / 2, s, size, smooth(0.8, 2.5, s), (ux, uy) => field.lake(ux, uy), undefined, this.assets.materials, pwLake);
    }
    const pwRiver: PaintedRiver | null = pwm?.riverWater && pwm.lakeWater && pwm.shoreGravel ? { water: pwm.riverWater, deep: pwm.lakeWater, gravel: pwm.shoreGravel } : null;
    if (waterways.rivers.length) paintRivers(g, X0, top, R, s, size, streamAlpha, waterways.rivers, lakeWater, this.assets.materials, pwRiver);
    if (!region) return;
    // the water at a point (cells, x east / y north), exactly as the lakes painted it: trees, boulders and the lakes' own
    // stones are placed against it
    const bridgeFoot = (x: number, y: number, half = 0) => bridgeCovers(waterways.bridges, x, y, half);
    const water = (x: number, y: number) => {
      if (bridgeFoot(x, y)) return 2;
      const river = riverFieldAt(waterways.rivers, x, y);
      return Math.max(lakeWater(x, y), river > 0 ? LAKE_EDGE + river : 0);
    };
    const trees0 = treeAlpha(s);
    // painted world v2: the painted shore relief along the lakes' edges, under the stones, trees and cities
    if (this.assets.painted && trees0 >= MIN_ALPHA) this.shoreRelief(g, X0, top, R, s, size, trees0, lakeAt, lakeWater, waterways, castleAt);
    if (trees0 >= MIN_ALPHA) this.lakeStones(g, X0, top, R, s, size, trees0, lakeAt, water);
    // the rest of the game's terrain, cell by cell (with the border cells, so pieces leaning into this tile are drawn): the
    // ravines, then the trees and boulders front to back — painted detail that fades out with the zoom (treeAlpha)
    const trees = trees0;
    const sprites: { im: HTMLImageElement; x: number; y: number; w: number; h: number; a: number }[] = [];
    // the ravines: one painted gorge joining neighbouring ravine cells (as the game's own tiles join them), under the trees
    const pwRavine: PaintedRavine | null = pwm?.gorgeWallLit && pwm.gorgeWallShadow && pwm.gorgeFloor
      ? { lit: pwm.gorgeWallLit, shadow: pwm.gorgeWallShadow, floor: pwm.gorgeFloor, walls: this.assets.painted!.walls } : null;
    if (trees >= MIN_ALPHA) paintRavines(g, X0, top, R, s, size, code, (gx, gy) => castleAt(gx, gy) || lakeAt(gx, gy) > 0, trees, this.assets.boulders, sprites, this.assets.materials, bridgeFoot, pwRavine);
    // The crossing is decoration at bank height, below depth-sorted scenery and all cities, with no new hit target.
    for (const bridge of waterways.bridges) this.bridge(g, bridge, X0, top, s, trees);
    for (let y = top; y >= Y0 - 1; y--) {
      for (let x = X0 - 1; x <= X0 + R; x++) {
        const wx = wrap(x, size);
        const wy = wrap(y, size);
        const t = code(wx, wy);
        if (trees < MIN_ALPHA) continue;
        if (scene.cities.has(key(wx, wy))) continue;
        // painted world v2: a few reeds on the damp banks of the lakes and streams
        if (this.assets.painted && t !== 2 && t !== 4 && (nearLake(terrain, wx, wy) || waterways.rivers.length)) {
          const [rx, ry] = cellPx(x, y);
          this.reeds(sprites, rx, ry, s, wx, wy, (fx, fy) => water(x + fx, y + 1 - fy), trees);
        }
        if (t === 0 || t === 3) continue;
        const [ox, oy] = cellPx(x, y);
        // nothing stands in the water: a cell beside a lake checks each piece's footing against the painted shore
        const wet = waterways.rivers.length || waterways.bridges.length || nearLake(terrain, wx, wy)
          ? (fx: number, fy: number) => water(x + fx, y + 1 - fy) : null;
        if (t === 1) this.forestCell(sprites, ox, oy, s, wx, wy, terrain, trees, wet);
        else if (t === 2) this.mountainCell(sprites, ox, oy, s, wx, wy, trees, wet);
      }
    }
    sprites.sort((a, b) => a.y + a.h - (b.y + b.h));
    g.imageSmoothingQuality = "medium";
    for (const sp of sprites) {
      g.globalAlpha = sp.a;
      g.drawImage(sp.im, sp.x, sp.y, sp.w, sp.h);
    }
    g.globalAlpha = 1;
  }

  /** The ground texture's mean colour (made once): what the texture reads as from far away. */
  private textureAverage(src: CanvasImageSource): string {
    if (this.texAvg && this.texAvgOf === src) return this.texAvg;
    this.texAvgOf = src;
    let avg = "rgb(98,104,70)";
    try {
      const c = document.createElement("canvas");
      c.width = c.height = 48;
      const g = c.getContext("2d", { willReadFrequently: true })!;
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = "high";
      g.drawImage(src, 0, 0, 48, 48);
      const d = g.getImageData(0, 0, 48, 48).data;
      let r = 0, gg = 0, b = 0;
      for (let i = 0; i < d.length; i += 4) { r += d[i]; gg += d[i + 1]; b += d[i + 2]; }
      const n = d.length / 4;
      avg = `rgb(${Math.round(r / n)},${Math.round(gg / n)},${Math.round(b / n)})`;
    } catch {
      /* keep the fallback */
    }
    this.texAvg = avg;
    return avg;
  }

  /** The patch-level forest / lake field for a patch size (kept while the terrain stays the same). */
  private densityField(terrain: TerrainCache, P: number, size: number): DensityField {
    if (this.fieldsFor !== terrain) {
      this.fields.clear();
      this.fieldsFor = terrain;
    }
    let f = this.fields.get(P);
    if (!f) {
      f = new DensityField(terrain, P, size);
      this.fields.set(P, f);
    }
    return f;
  }

  private streams(scene: Scene): RiverNetwork | null {
    const terrain = scene.terrain;
    if (!terrain) return null;
    if (!this.riverNetwork || this.riversFor !== terrain || this.riversVersion !== scene.cityVersion || this.riverNetwork.size !== scene.size) {
      this.riversFor = terrain;
      this.riversVersion = scene.cityVersion;
      const cities = scene.cities;
      const code = this.terrainCode(scene);
      this.riverNetwork = new RiverNetwork(scene.size, terrain.seed, code, (x, y) => cities.has(key(x, y)));
    }
    return this.riverNetwork;
  }

  /** The terrain code as the map paints it: the game's own, plus painted world v2's cosmetic gorge extensions (made
   *  once per terrain, cities and world size, like the streams). Without the painted world, the game's alone. */
  private terrainCode(scene: Scene): (x: number, y: number) => number {
    const terrain = scene.terrain!;
    // (only with the painted gorges themselves: without their art the earlier gorges stand as they were)
    if (!this.assets.painted?.materials.gorgeWallLit) return (x, y) => terrain.at(x, y);
    if (!this.ravineExt || this.extFor !== terrain || this.extVersion !== scene.cityVersion || this.ravineExt.size !== scene.size) {
      this.extFor = terrain;
      this.extVersion = scene.cityVersion;
      const cities = scene.cities;
      this.ravineExt = new RavineExtension(scene.size, terrain.seed, (x, y) => terrain.at(x, y), (x, y) => cities.has(key(x, y)));
    }
    const ext = this.ravineExt;
    return (x, y) => ext.code(x, y);
  }

  /** Debug geometry uses the same cached routes and crossing rules as the ground painter. */
  waterwaysIn(x0: number, y0: number, x1: number, y1: number): { rivers: RiverSeg[]; bridges: Bridge[] } {
    const scene = this.waterwayScene;
    if (!scene?.terrain || !scene.layers.forest) return { rivers: [], bridges: [] };
    return this.waterwayGeometry(x0, y0, x1, y1, scene, true);
  }

  private waterwayGeometry(x0: number, y0: number, x1: number, y1: number, scene: Scene, crossings: boolean): { rivers: RiverSeg[]; bridges: Bridge[] } {
    const network = this.streams(scene);
    const terrain = scene.terrain;
    if (!network || !terrain) return { rivers: [], bridges: [] };
    const rivers = network.segments(x0, y0, x1, y1);
    if (!crossings) return { rivers, bridges: [] };
    const size = scene.size;
    const castle = (x: number, y: number) => !!tierOf(scene.cities.get(key(wrap(x, size), wrap(y, size)))?.level ?? 0);
    const lake = (x: number, y: number) => terrain.at(x, y) === 3 && !scene.cities.has(key(wrap(x, size), wrap(y, size))) ? 1 : 0;
    const water = (x: number, y: number) => lakeFieldAt(x, y, size, lake, castle);
    const blocked = (x: number, y: number) => castle(x, y) || lake(x, y) > 0;
    const code = this.terrainCode(scene);
    const rifts = ravineSegments(x0 - 12, y0 - 12, x1 + 12, y1 + 12, size, (x, y) => code(wrap(x, size), wrap(y, size)) === 4, blocked);
    // painted world v2: wider gorges (their bridges are longer), crossings a little sparser
    const pwOn = !!this.assets.painted?.materials.gorgeWallLit;
    const rw = pwOn ? PW_RAVINE_W : RAVINE_W;
    const excluded = (x: number, y: number) => terrain.at(Math.floor(x), Math.floor(y)) === 2 ||
      water(x, y) > LAKE_EDGE - 0.05 || cityFootprintAt(x, y, castle);
    const bridges = findBridges(x0, y0, x1, y1, {
      size, seed: terrain.seed, minSpacing: pwOn ? 3.75 : 3,
      sources: [
        { type: "rift", segments: () => rifts, gapHalfWidth: (x, y) => rw.lip * (0.62 + 0.6 * lakeWobble(x, y, size, 0.7, 1201)), chance: 0.3 },
        { type: "river", segments: (a, b, c, d) => network.segments(a, b, c, d), chance: 0.32 },
      ],
      excluded,
      isDry: (x, y) => !excluded(x, y) && network.waterAt(x, y) === 0 && ravineFieldDistance(rifts, x, y, size) > rw.lip * 1.1,
    });
    return { rivers, bridges };
  }

  /** Painted world v2: reeds standing on a lake's or stream's damp bank — two spots a cell, each kept only where the
   *  painted water field puts it on the bank's outer band (wet ground, never in the water), sparsely. */
  private reeds(out: { im: HTMLImageElement; x: number; y: number; w: number; h: number; a: number }[], ox: number, oy: number, s: number, wx: number, wy: number, wet: (fx: number, fy: number) => number, alpha: number) {
    const pics = this.assets.painted?.reeds;
    if (!pics) return;
    for (let i = 0; i < 2; i++) {
      if (cellHash(wx, wy, 1601 + i) > 0.42) continue;
      const fx = 0.12 + 0.76 * cellHash(wx, wy, 1611 + i);
      const fy = 0.15 + 0.75 * cellHash(wx, wy, 1621 + i);
      const f = wet(fx, fy);
      if (f < 0.3 || f > LAKE_EDGE - 0.05) continue;
      const ri = Math.min(REED_COUNT - 1, Math.floor(cellHash(wx, wy, 1631 + i) * REED_COUNT));
      const im = pics[ri];
      if (!im) continue;
      const r = sceneryRect("reed", ri, ox + fx * s, oy + fy * s, s, 0.75 + 0.4 * cellHash(wx, wy, 1641 + i));
      out.push({ im, ...r, a: alpha });
    }
  }

  /** Painted world v2: the shoreline relief. Along each lake's edge, at spaced points (a shore cell's chance, then a
   *  local priority within SHORE_SPACING cells — both from world cells only, so every tile chooses the same), the shore
   *  patch whose measured water side matches the painted shore's own normal there (pickShore: never rotated or mirrored)
   *  is placed with its waterline on the edge, and composed clipped to the painted shore band — so the runtime field owns
   *  where water is, the patches add only rocks, grass and broken water along it. Kept off castles' dry footings, stream
   *  mouths and bridges. */
  private shoreRelief(g: CanvasRenderingContext2D, X0: number, top: number, R: number, s: number, size: number, alpha: number, lakeAt: (gx: number, gy: number) => number, field: (x: number, y: number) => number, waterways: { rivers: RiverSeg[]; bridges: Bridge[] }, castleAt: (gx: number, gy: number) => boolean) {
    const pics = this.assets.painted?.shores;
    if (!pics?.some(Boolean)) return;
    const M = 3;
    const cands: { x: number; y: number; p: number; i: number }[] = [];
    const steps: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
    for (let y = top - R - M; y < top + M; y++) {
      for (let x = X0 - M; x < X0 + R + M; x++) {
        if (!lakeAt(x, y)) continue;
        const wx = wrap(x, size), wy = wrap(y, size);
        if (cellHash(wx, wy, 1501) > SHORE_CHANCE) continue;
        const dry = steps.filter(([dx, dy]) => !lakeAt(x + dx, y + dy) && !castleAt(x + dx, y + dy));
        if (!dry.length) continue;
        const [dx, dy] = dry[Math.floor(cellHash(wx, wy, 1503) * dry.length) % dry.length];
        // walk from the cell's middle toward its dry side to the painted waterline
        let ex = NaN, ey = NaN;
        for (let d = 0; d <= 1.3; d += 0.04) {
          const px = x + 0.5 + dx * d, py = y + 0.5 + dy * d;
          if (field(px, py) < LAKE_EDGE) { ex = px; ey = py; break; }
        }
        if (!Number.isFinite(ex)) continue;
        const e = 0.06;
        const gx = field(ex + e, ey) - field(ex - e, ey), gy = field(ex, ey + e) - field(ex, ey - e);
        const gl = Math.hypot(gx, gy);
        if (gl < 1e-4) continue;
        // the water's side on screen: world (gx, gy) → image (gx, −gy)
        const i = pickShore(gx / gl, -gy / gl);
        if (i === null || !pics[i]) continue;
        if (cityFootprintAt(ex, ey, castleAt) || bridgeCovers(waterways.bridges, ex, ey, 0.55)) continue;
        if ([[0, 0], [0.6, 0], [-0.6, 0], [0, 0.6], [0, -0.6]].some(([ox, oy]) => riverFieldAt(waterways.rivers, ex + ox, ey + oy) > 0)) continue;
        cands.push({ x: ex, y: ey, p: cellHash(wx, wy, 1507), i });
      }
    }
    const kept = cands.filter((c) => !cands.some((o) => o !== c && Math.hypot(o.x - c.x, o.y - c.y) < SHORE_SPACING && (o.p > c.p || (o.p === c.p && (o.x < c.x || (o.x === c.x && o.y < c.y))))));
    const placed: { im: HTMLImageElement; x: number; y: number; w: number; h: number }[] = [];
    for (const c of kept) {
      const m = shoreMeta(c.i);
      const k = (SHORE_LENGTH * s) / m.length;
      const sx = (c.x - X0) * s, sy = (top - c.y) * s;
      if (sx < -s * 1.5 || sx > R * s + s * 1.5 || sy < -s * 1.5 || sy > R * s + s * 1.5) continue;
      placed.push({ im: pics[c.i]!, x: sx - m.anchor[0] * k, y: sy - m.anchor[1] * k, w: m.w * k, h: m.h * k });
    }
    if (!placed.length) return;
    // the clip: the shore band — the bank and the shallows — fading out into the open water
    const bb = reliefBounds(placed, R, s);
    if (!bb) return;
    const K = 8;
    const N = R * K;
    const mask = new Uint8ClampedArray(N * N);
    const i0 = Math.max(0, Math.floor((bb.x0 / s) * K)), i1 = Math.min(N, Math.ceil((bb.x1 / s) * K));
    const j0 = Math.max(0, Math.floor((bb.y0 / s) * K)), j1 = Math.min(N, Math.ceil((bb.y1 / s) * K));
    for (let j = j0; j < j1; j++) {
      const wy = top - (j + 0.5) / K;
      for (let i = i0; i < i1; i++) {
        const f = field(X0 + (i + 0.5) / K, wy);
        if (f <= 0.03) continue;
        mask[j * N + i] = 255 * smooth(0.03, 0.15, f) * (1 - smooth(0.85, 1.25, f));
      }
    }
    placed.sort((a, b) => a.y - b.y);
    drawClipped(g, placed, mask, N, R, s, alpha);
  }

  /** A crossing: painted world v2's timber bridge whose authored deck runs closest to the crossing (pickBridge — never
   *  rotated or mirrored), its measured ends on the two banks; the drawn timber where no painted view fits. */
  private bridge(g: CanvasRenderingContext2D, b: Bridge, X0: number, top: number, s: number, alpha: number) {
    const pw = this.assets.painted;
    const i = pw ? pickBridge(b.type, b.ux, b.uy) : null;
    const im = i !== null ? pw!.bridges[i] : null;
    if (i === null || !im || alpha <= 0 || s < 20) {
      paintBridge(g, b, X0, top, s, alpha);
      return;
    }
    const r = bridgeRect(i, (b.x - X0) * s, (top - b.y) * s, b.halfLength, s);
    g.save();
    g.globalAlpha = alpha;
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = "high";
    g.drawImage(im, r.x, r.y, r.w, r.h);
    g.restore();
  }

  /** Painted tree clusters on a forest cell (the reference's layout: 2–4 per cell by how wooded its neighbours are), at a
   *  fixed per-cell jitter so they never move. */
  private forestCell(out: { im: HTMLImageElement; x: number; y: number; w: number; h: number; a: number }[], ox: number, oy: number, s: number, wx: number, wy: number, terrain: TerrainCache, alpha: number, wet: Wet) {
    let around = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) if ((dx || dy) && terrain.at(wx + dx, wy + dy) === 1) around++;
    const pw = this.assets.painted;
    if (pw && pw.trees.every(Boolean)) {
      // painted world v2: single painted trees, 0–3 a cell (paintedTrees) — about half the crowns the clusters had
      const n = paintedTrees(around, cellHash(wx, wy, 97));
      for (let i = 0; i < n; i++) {
        const fx = 0.14 + cellHash(wx, wy, 31 + i) * 0.72;
        const fy = 0.24 + cellHash(wx, wy, 61 + i) * 0.66;
        const ti = paintedTreeKind(cellHash(wx, wy, 101 + i));
        const r = sceneryRect("tree", ti, ox + fx * s, oy + fy * s, s, 0.82 + 0.3 * cellHash(wx, wy, 145 + i));
        if (wet && onWater(wet, fx, fy, r.w / s / 2)) continue;
        out.push({ im: pw.trees[ti]!, ...r, a: alpha });
      }
      return;
    }
    const n = forestClusters(around, cellHash(wx, wy, 97));
    const pics = this.assets.trees;
    for (let i = 0; i < n; i++) {
      const fx = 0.16 + cellHash(wx, wy, 31 + i) * 0.66;
      const fy = 0.24 + cellHash(wx, wy, 61 + i) * 0.63;
      const im = pics[Math.floor(cellHash(wx, wy, 101 + i) * pics.length) % pics.length];
      const tw = s * (0.38 + cellHash(wx, wy, 145 + i) * 0.17) * SCENERY_SCALE;
      if (wet && onWater(wet, fx, fy, tw / s / 2)) continue;
      const th = (tw * im.height) / im.width;
      const px = ox + fx * s;
      const py = oy + fy * s;
      out.push({ im, x: px - tw / 2, y: py - th * 0.84, w: tw, h: th, a: alpha });
    }
  }

  private mountainCell(out: { im: HTMLImageElement; x: number; y: number; w: number; h: number; a: number }[], ox: number, oy: number, s: number, wx: number, wy: number, alpha: number, wet: Wet) {
    const pw = this.assets.painted;
    if (pw && pw.rocks.every(Boolean)) {
      // painted world v2: one or two painted rocks, smaller than the boulder clusters they replace
      const n = cellHash(wx, wy, 199) < 0.55 ? 1 : 2;
      for (let i = 0; i < n; i++) {
        const fx = 0.2 + 0.6 * cellHash(wx, wy, 200 + i);
        const fy = 0.35 + 0.5 * cellHash(wx, wy, 210 + i);
        const ri = Math.min(ROCK_COUNT - 1, Math.floor(cellHash(wx, wy, 230 + i) * ROCK_COUNT));
        const r = sceneryRect("rock", ri, ox + fx * s, oy + fy * s, s, 0.7 + 0.45 * cellHash(wx, wy, 220 + i));
        if (wet && onWater(wet, fx, fy, r.w / s / 2)) continue;
        out.push({ im: pw.rocks[ri]!, ...r, a: alpha });
      }
      return;
    }
    const im = this.assets.boulders;
    for (let i = 0; i < 2; i++) {
      const fx = 0.2 + 0.6 * cellHash(wx, wy, 200 + i);
      const fy = 0.35 + 0.5 * cellHash(wx, wy, 210 + i);
      const pw = s * (0.5 + 0.2 * cellHash(wx, wy, 220 + i)) * SCENERY_SCALE;
      if (wet && onWater(wet, fx, fy, pw / s / 2)) continue;
      const px = ox + fx * s;
      const py = oy + fy * s;
      const ph = pw * (im.height / im.width);
      out.push({ im, x: px - pw / 2, y: py - ph * 0.86, w: pw, h: ph, a: alpha });
    }
  }

  /** A few stones breaking the surface near the shores (Oct 14 2026): per lake cell up to two, kept where the painted
   *  water is shallow — inside the shallows line, not out in the deep — each with a pale ripple ring. */
  private lakeStones(g: CanvasRenderingContext2D, X0: number, top: number, R: number, s: number, size: number, alpha: number, lakeAt: (gx: number, gy: number) => number, water: (x: number, y: number) => number) {
    g.save();
    g.globalAlpha = alpha;
    for (let y = top; y >= top - R - 1; y--) {
      for (let x = X0 - 1; x <= X0 + R; x++) {
        if (!lakeAt(x, y)) continue;
        const wx = wrap(x, size);
        const wy = wrap(y, size);
        for (let i = 0; i < 2; i++) {
          if (cellHash(wx, wy, 400 + i) > STONE_CHANCE) continue;
          const fx = 0.12 + 0.76 * cellHash(wx, wy, 410 + i);
          const fy = 0.12 + 0.76 * cellHash(wx, wy, 420 + i);
          const f = water(x + fx, y + 1 - fy);
          if (f < 0.56 || f > 1.3) continue; // in the water, close to the shore
          const cx = (x + fx - X0) * s;
          const cy = (top - (y + 1 - fy)) * s;
          const n = 1 + Math.floor(cellHash(wx, wy, 430 + i) * 3); // a stone, or a little group
          const acc = this.assets.materials?.lakeStone;
          if (acc) {
            // the painted stone cluster (Oct 14 2026): one per group, its painted width the group's spread, its contact
            // point at the group's main stone; a restrained ripple ring round it (the PNG has no water of its own)
            const r = s * (0.03 + 0.035 * cellHash(wx, wy, 440 + i * 3));
            const pw = r * (n === 1 ? 2.2 : n === 2 ? 2.8 : 3.4) * SCENERY_SCALE;
            g.lineWidth = Math.max(0.8, s * 0.008) * SCENERY_SCALE;
            g.strokeStyle = "rgba(196,212,208,0.22)";
            g.beginPath();
            g.ellipse(cx, cy + r * 0.1, pw * 0.72, pw * 0.26, 0, 0, Math.PI * 2);
            g.stroke();
            const rr = accentRect(acc, pw, cx, cy + r * 0.25);
            g.imageSmoothingEnabled = true;
            g.imageSmoothingQuality = "high";
            g.drawImage(acc.im, rr.x, rr.y, rr.w, rr.h);
            continue;
          }
          for (let k = 0; k < n; k++) {
            const r = s * (0.03 + 0.035 * cellHash(wx, wy, 440 + i * 3 + k)) * (k ? 0.7 : 1) * SCENERY_SCALE;
            const sx = cx + (k ? (cellHash(wx, wy, 450 + i * 3 + k) - 0.5) * s * 0.16 : 0);
            const sy = cy + (k ? (cellHash(wx, wy, 460 + i * 3 + k) - 0.5) * s * 0.09 : 0);
            paintStone(g, sx, sy, r, s * SCENERY_SCALE);
          }
        }
      }
    }
    g.restore();
  }

  /** Drop the least recently used ground tiles beyond TILE_KEEP (down to TILE_KEEP_AFTER) or the pixel budget; the
   *  tiles this frame drew always stay. */
  private evictGround() {
    let pixels = 0;
    for (const t of this.ground.values()) pixels += t.canvas.width * t.canvas.height;
    if (this.ground.size <= TILE_KEEP && pixels <= TILE_PIXEL_BUDGET) return;
    const target = this.ground.size > TILE_KEEP ? TILE_KEEP_AFTER : TILE_KEEP;
    const entries = [...this.lastUse.entries()].sort((a, b) => a[1] - b[1]);
    for (const [k, used] of entries) {
      if (used === this.frame) break;
      if (this.ground.size <= target && pixels <= TILE_PIXEL_BUDGET) break;
      const t = this.ground.get(k);
      if (t) pixels -= t.canvas.width * t.canvas.height;
      this.dropTile(k);
    }
  }

  private dropTile(k: string) {
    const t = this.ground.get(k);
    // let the browser free the bitmap now rather than at garbage collection (iOS Safari caps total canvas memory)
    if (t) t.canvas.width = t.canvas.height = 0;
    this.ground.delete(k);
    this.lastUse.delete(k);
  }

  // ---------------------------------------------------------------------------------------------- region: castles
  /** Every city the region frame draws, in draw order (north to south, then west to east), with its castle's rect. */
  private cityLayout(view: View, w: number, h: number, scene: Scene): CityItem[] {
    const { size } = scene;
    const s = view.s;
    // two cells of margin: a castle reaches 0.72 cell north of its cell, a selected castle's glow further
    const vis = visibleCells(view, w, h, 2);
    const sel = scene.selected;
    const owner = scene.ownerCities?.size ? scene.ownerCities : null;
    const out: CityItem[] = [];
    for (let y = vis.y1; y >= vis.y0; y--) {
      for (let x = vis.x0; x <= vis.x1; x++) {
        const wx = wrap(x, size);
        const wy = wrap(y, size);
        const k = key(wx, wy);
        const c = scene.cities.get(k);
        if (!c) continue;
        const tier = tierOf(c.level);
        if (!tier) continue; // level 0: an empty cell
        const [px, py] = cellCentrePx(view, w, h, size, x, y);
        const rel = relationOf(c, scene.relations);
        const family = familyOf(rel);
        const selected = !!sel && sel[0] === wx && sel[1] === wy;
        // the selected city's owner's other cities: lit in their own colour (another player's castles: never a stockade,
        // nor one the map knows as yours — the player's city list can lag a conquest by a few minutes)
        const lit = owner && !selected && family === "castle" && rel !== "you" && owner.has(k) ? REL_COLORS[rel].body : null;
        const sf = art.map.slateFire;
        if (family === "castle" && this.assets.painted) {
          const url = castleUrl(rel as CityRelation, tier);
          if (!this.sprites.failed(url)) {
            out.push({ c, tier, rel, family, px, py, box: paintedCastleBox(rel as CityRelation, tier, s, px, py), selected, lit, painted: true, url, key: `painted:${rel}:${tier}` });
            continue;
          }
        }
        const url = family === "npc" ? sf.npc(tier) : sf.castle(rel as CityRelation, tier);
        out.push({ c, tier, rel, family, px, py, box: cityBox(family, tier, s, px, py), selected, lit, painted: false, url, key: `${family}:${tier}` });
      }
    }
    return out;
  }

  private drawRegion(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number, view: View, scene: Scene) {
    const s = view.s;
    const list = this.cityLayout(view, w, h, scene);
    const a = castleAlpha(s);
    const pm = pointMix(s);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    // the halos of the owner's lit markers first, under every castle and marker, so a halo never washes over a neighbour
    // (a marker shows where its castle isn't drawn in full: while the sprites fade, or its PNG isn't here)
    for (const it of list) {
      if (!it.lit) continue;
      const ma = a > 0 && this.sprites.ready(spriteUrl(it)) ? 1 - a : 1;
      paintHalo(ctx, this.halo(it.lit), it.px, it.py, markerHalf(s, it.tier), pointRadius(pointKind(it.rel, it.c.guildId), it.tier), pm, ma);
    }
    for (const it of list) {
      // a castle whose PNG hasn't arrived yet stands as its marker
      const drawn = a > 0 ? this.castle(ctx, it, a, s) : null;
      const ma = drawn ? 1 - a : 1;
      if (ma > 0) {
        const color = it.rel === "barbarian" ? BARBARIAN_MARK : REL_COLORS[it.rel].body!;
        const kind = pointKind(it.rel, it.c.guildId);
        const mark = it.selected ? SELECTED_MARK : it.lit ? ownerMark(color) : null;
        paintMarker(ctx, it.px, it.py, markerHalf(s, it.tier), color, ma, pm, pointRadius(kind, it.tier), pointAlpha(kind, !!mark), mark, dpr);
      }
      this.frameInfo?.push({ x: it.c.x, y: it.c.y, rel: it.rel, family: it.family, tier: it.tier, box: { x: it.box.x, y: it.box.y, w: it.box.w, h: it.box.h }, sprite: drawn?.url ?? null, flag: drawn?.flag ?? null, glow: drawn?.glow ?? null, shield: drawn?.shield ?? null, marker: ma > 0 });
      if (it.lit) this.frameLit?.push({ x: it.c.x, y: it.c.y, color: it.lit, glow: drawn?.litGlow ?? null, marker: ma > 0 });
    }
    ctx.globalAlpha = 1;
    for (const it of list) this.nameplate(ctx, it, scene, s);
  }

  /** The city's sprite — a castle, or an NPC's stockade — (and its glow underneath: the ivory when selected, its own colour
   *  when it is one of the selected city's owner's other castles), a castle's pennant, the status shield; null if its PNG
   *  isn't here yet. */
  private castle(ctx: CanvasRenderingContext2D, it: CityItem, a: number, s: number): DrawnCastle | null {
    const sf = art.map.slateFire;
    const npc = it.family === "npc";
    // an NPC: its tier's Log Stockade as authored (no tint, no fade, no pennant); its glow is baked from it as it arrives
    const url = spriteUrl(it);
    const pw = it.painted;
    const img = this.sprites.get(url, pw ? { family: it.family, tier: it.tier, key: it.key, boost: false } : { family: it.family, tier: it.tier }, npc);
    if (!img) return null;
    // a painted castle's glow is baked from its own silhouette, the first time it is selected or lit
    if (pw && (it.selected || it.lit)) this.sprites.wantAura(it.key, img);
    const b = it.box;
    let glow: { x: number; y: number; w: number; h: number } | null = null;
    let litGlow: { x: number; y: number; w: number; h: number } | null = null;
    if (it.selected) {
      // the broad ivory silhouette glow, under this sprite and over the cities behind it (drawn at its depth, not on top)
      const gi = npc || pw ? this.sprites.aura(it.family, it.tier, it.key) : this.sprites.get(sf.glow(it.tier));
      if (gi) {
        glow = gi instanceof HTMLCanvasElement ? glowBox(b, gi.width, gi.height) : glowBox(b, gi.naturalWidth, gi.naturalHeight);
        ctx.globalAlpha = a;
        ctx.drawImage(gi, glow.x, glow.y, glow.w, glow.h);
      }
    } else if (it.lit) {
      // the same glow in this castle's own colour, placed and layered as the ivory one
      const t = pw ? this.sprites.tintedCanvas(it.key, this.sprites.aura(it.family, it.tier, it.key), it.lit) : this.sprites.tinted(sf.glow(it.tier), it.tier, it.lit);
      if (t) {
        litGlow = glowBox(b, t.w, t.h);
        ctx.globalAlpha = a;
        ctx.drawImage(t.canvas, litGlow.x, litGlow.y, litGlow.w, litGlow.h);
      }
    }
    ctx.globalAlpha = a;
    ctx.drawImage(this.sprites.display(url) ?? img, b.x, b.y, b.w, b.h);
    let flag: { x: number; y: number; w: number; h: number } | null = null;
    if (!npc) flag = this.flag(ctx, it, it.rel as CityRelation, a, s);
    let shield: { x: number; y: number; h: number } | null = null;
    if (it.c.beginnerProtection || it.c.truce) {
      // beginner protection (steel) wins over truce (ivory), as before; on the family's own foundation
      const st = statusShield(it.tier, s, b, it.family, pw ? paintedStatusMount(it.rel as CityRelation, it.tier) : undefined);
      const im = it.c.beginnerProtection ? this.assets.shieldProtection : this.assets.shieldTruce;
      if (im) this.shield(ctx, im, st.x, st.y, st.h);
      else this.badge(ctx, st.x, st.y, Math.max(6, s * 0.16) * (npc ? NPC_SHIELD_SCALE : 1), it.c.beginnerProtection ? "#8EC6FF" : "#F2EEE4");
      shield = st;
    }
    ctx.globalAlpha = 1;
    return { url, flag, glow, litGlow, shield };
  }

  /** The painted pennant on its tower: pole, cloth, highlight and finial at the reference's geometry, the whole assembly
   *  scaled by one half about the roof mount. */
  private flag(ctx: CanvasRenderingContext2D, it: CityItem, rel: CityRelation, a: number, s: number) {
    const pc = it.painted ? this.assets.painted?.pennants[rel] ?? null : null;
    const cloth = pc ?? this.assets.flags[rel];
    const f = it.painted
      ? flagGeom(it.tier, rel, s, it.box, paintedFlagMount(rel, it.tier), pc ? pennantAspect(rel) : undefined)
      : flagGeom(it.tier, rel, s, it.box);
    ctx.save();
    ctx.globalAlpha = a;
    ctx.translate(f.x, f.bottom);
    ctx.scale(FLAG_SCALE, FLAG_SCALE);
    ctx.translate(-f.x, -f.bottom);
    ctx.lineCap = "round";
    ctx.lineWidth = f.pole;
    ctx.strokeStyle = POLE_COLOR;
    ctx.beginPath(); ctx.moveTo(f.x, f.bottom); ctx.lineTo(f.x, f.top - 1); ctx.stroke();
    ctx.lineWidth = f.pole * 0.4;
    ctx.strokeStyle = POLE_HIGHLIGHT;
    ctx.beginPath(); ctx.moveTo(f.x - f.pole * 0.12, f.bottom - 1); ctx.lineTo(f.x - f.pole * 0.12, f.top); ctx.stroke();
    ctx.drawImage(cloth, f.clothX, f.clothY, f.w, f.h);
    if (f.finial !== null) {
      const fi = f.finial;
      ctx.fillStyle = FINIAL_COLOR;
      ctx.strokeStyle = FINIAL_OUTLINE;
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(f.x, f.top - fi * 2.8);
      ctx.lineTo(f.x + fi, f.top - fi * 1.25);
      ctx.lineTo(f.x, f.top - 0.1);
      ctx.lineTo(f.x - fi, f.top - fi * 1.25);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
    return flagScreenRect(f);
  }

  /** The painted shield (beginner protection / truce) on the castle's foundation corner, centred on (x, y), h px tall,
   *  with a soft dark shadow so it reads against the grass and the walls. */
  private shield(ctx: CanvasRenderingContext2D, im: HTMLImageElement, x: number, y: number, h: number) {
    const w = h * (im.width / im.height);
    ctx.save();
    ctx.shadowColor = "rgba(8,6,4,0.75)";
    ctx.shadowBlur = Math.max(3, h * 0.18);
    ctx.shadowOffsetX = h * 0.05;
    ctx.shadowOffsetY = h * 0.07;
    ctx.drawImage(im, x - w / 2, y - h / 2, w, h);
    ctx.restore();
  }

  private badge(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r * 0.85, y - r * 0.6);
    ctx.lineTo(x + r * 0.7, y + r * 0.3);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r * 0.7, y + r * 0.3);
    ctx.lineTo(x - r * 0.85, y - r * 0.6);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = "rgba(20,14,8,0.85)";
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
  }

  private nameplate(ctx: CanvasRenderingContext2D, it: CityItem, scene: Scene, s: number) {
    // Only with the All nameplates layer on: every city zoomed in, your own at any zoom. Selecting a city adds no plate
    // (Ben, Oct 12 2026: its glow and its sheet say which city it is).
    if (!scene.layers.allNames) return;
    const plate = scene.names.get(key(it.c.x, it.c.y));
    if (!plate || !plate.name) return;
    if (!(s >= 56 || plate.own)) return;
    const ty = mapType();
    const fs = Math.max(10, Math.min(12.5, s * 0.22)) * ty.tz;
    const fs2 = fs * 1.05;
    ctx.save();
    ctx.font = `600 ${fs}px ${ty.caps}`;
    const name = plate.name.toUpperCase();
    const w1 = ctx.measureText(name).width;
    ctx.font = `500 ${fs2}px ${ty.text}`;
    const w2 = plate.sub ? ctx.measureText(plate.sub).width : 0;
    const pad = 8;
    const w = Math.max(w1, w2) + pad * 2;
    const h = plate.sub ? fs + fs2 + 12 : fs + 10;
    const x = Math.round(it.px - w / 2);
    // under the painted foundation — the family's own (or under the marker when the sprites have faded out)
    const castleBottom = it.box.baseY - it.box.h * (1 - (it.box.paintBottom ?? paintedBottom(it.family, it.tier)));
    const markerBottom = it.py + markerHalf(s, it.tier);
    const y = Math.round((castleAlpha(s) >= 0.5 ? castleBottom : markerBottom) + 3);
    ctx.fillStyle = "rgba(11,14,17,0.88)";
    ctx.strokeStyle = plate.own ? "rgba(201,162,74,0.9)" : "rgba(120,96,52,0.8)";
    ctx.lineWidth = 1;
    roundRect(ctx, x, y, w, h, 5);
    ctx.fill();
    ctx.stroke();
    if (plate.own) {
      ctx.fillStyle = "#C9A24A";
      ctx.beginPath();
      ctx.moveTo(x, y + h / 2 - 3.4);
      ctx.lineTo(x + 3.4, y + h / 2);
      ctx.lineTo(x, y + h / 2 + 3.4);
      ctx.lineTo(x - 3.4, y + h / 2);
      ctx.closePath();
      ctx.fill();
    }
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = plate.own ? "#F3E9D2" : "#E6DCC3";
    ctx.font = `600 ${fs}px ${ty.caps}`;
    ctx.fillText(name, it.px, y + fs + 3);
    this.framePlates?.push(plate.name);
    this.framePlateBoxes?.push({ name: plate.name, x, y, w, h });
    if (plate.sub) {
      ctx.fillStyle = "#A99E8A";
      ctx.font = `500 ${fs2}px ${ty.text}`;
      ctx.fillText(plate.sub, it.px, y + fs + fs2 + 6);
    }
    ctx.restore();
  }

  // ---------------------------------------------------------------------------------------------- realm
  /** The cities north to south, then west to east (later markers overlap earlier ones, as the castles do). */
  private sortedCities(list: Cell[]): Cell[] {
    if (this.sortedFor !== list) {
      this.sorted = [...list].sort((a, b) => b.y - a.y || a.x - b.x);
      this.sortedFor = list;
    }
    return this.sorted;
  }

  private drawRealm(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number, view: View, scene: Scene) {
    const { size } = scene;
    const s = view.s;
    // When the whole world fits, the cities are drawn once — each at its copy nearest the centre, so in the one world-sized
    // window centred on the view — and the ground beyond that window (the next turn of the wrapping world) is shaded.
    const span = size * s;
    const wl = w / 2 - span / 2;
    const wt = h / 2 - span / 2;
    if (span < w || span < h) {
      ctx.fillStyle = "rgba(14,19,20,0.82)";
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.rect(wl, wt, span, span);
      ctx.fill("evenodd");
    }
    if (span < Math.max(w, h) * 1.4) {
      ctx.strokeStyle = "rgba(201,162,74,0.25)";
      ctx.lineWidth = 1;
      ctx.strokeRect(wl, wt, span, span);
    }
    const pm = pointMix(s);
    const own = scene.relations.own;
    const owner = scene.ownerCities?.size ? scene.ownerCities : null;
    type Item = { x: number; y: number; half: number; color: string; alpha: number; r: number; cx: number; cy: number };
    let selItem: Item | null = null;
    // the selected city's owner's other cities: their halos first, under every marker (so a halo never washes over a
    // neighbour), and their markers after the rest, so no neighbour covers them
    const litItems: Item[] = [];
    if (owner) {
      for (const k of owner) {
        const c = scene.cities.get(k);
        const tier = c ? tierOf(c.level) : null;
        if (!c || !tier || (scene.selected && scene.selected[0] === c.x && scene.selected[1] === c.y)) continue;
        const rel = relationOf(c, scene.relations);
        if (rel === "barbarian" || rel === "you") continue;
        const [px, py] = cellCentrePx(view, w, h, size, c.x, c.y);
        if (px < -30 || py < -30 || px > w + 30 || py > h + 30) continue;
        paintHalo(ctx, this.halo(scene.realmColor(c)), px, py, markerHalf(s, tier), pointRadius(pointKind(rel, c.guildId), tier), pm, 1);
      }
    }
    for (const c of this.sortedCities(scene.cityList)) {
      const tier = tierOf(c.level);
      if (!tier) continue;
      const [px, py] = cellCentrePx(view, w, h, size, c.x, c.y);
      if (px < -12 || py < -12 || px > w + 12 || py > h + 12) continue;
      const rel = relationOf(c, scene.relations);
      const color = rel === "barbarian" ? BARBARIAN_MARK : scene.realmColor(c);
      let alpha = 1;
      if (scene.isolateGid !== null && c.guildId !== scene.isolateGid && !own.has(key(c.x, c.y))) alpha = 0.16;
      if (rel === "barbarian") alpha *= 0.9;
      const half = markerHalf(s, tier);
      const kind = pointKind(rel, c.guildId);
      const r = pointRadius(kind, tier);
      if (scene.selected && scene.selected[0] === c.x && scene.selected[1] === c.y) {
        selItem = { x: px, y: py, half, color, alpha: Math.max(alpha, 0.85), r, cx: c.x, cy: c.y };
        continue;
      }
      if (owner && rel !== "barbarian" && rel !== "you" && owner.has(key(c.x, c.y))) {
        // lit like the selected city: shown even when guild isolation dims the rest
        litItems.push({ x: px, y: py, half, color, alpha: Math.max(alpha, 0.85), r, cx: c.x, cy: c.y });
        continue;
      }
      paintMarker(ctx, px, py, half, color, alpha, pm, r, pointAlpha(kind, false), null, dpr);
    }
    for (const it of litItems) {
      paintMarker(ctx, it.x, it.y, it.half, it.color, it.alpha, pm, it.r, 1, ownerMark(it.color), dpr);
      this.frameLit?.push({ x: it.cx, y: it.cy, color: it.color, glow: null, marker: true });
    }
    if (scene.home) {
      const [cx, cy] = cellCentrePx(view, w, h, size, scene.home[0], scene.home[1]);
      const d = Math.max(4, markerHalf(s, 4) * 2);
      ctx.save();
      ctx.strokeStyle = "rgba(237,214,157,0.95)";
      ctx.lineWidth = 2;
      ctx.shadowColor = "rgba(236,182,40,0.8)";
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(9, d * 0.8 + 4), 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.font = `700 ${10.5 * mapType().tz}px ${mapType().caps}`;
      ctx.textAlign = "center";
      const label = "YOU";
      const lw = ctx.measureText(label).width + 14;
      const ry = Math.max(9, d * 0.8 + 4);
      ctx.fillStyle = "rgba(11,14,17,0.9)";
      roundRect(ctx, cx - lw / 2, cy - ry - 24, lw, 17, 4);
      ctx.fill();
      ctx.strokeStyle = "rgba(201,162,74,0.9)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#F3E9D2";
      ctx.fillText(label, cx, cy - ry - 12);
      ctx.restore();
    }
    // the selected city on top: its own marker with the ivory edge and glow (no ring)
    if (selItem) paintMarker(ctx, selItem.x, selItem.y, selItem.half, selItem.color, selItem.alpha, pm, selItem.r, 1, SELECTED_MARK, dpr);
  }

  // ---------------------------------------------------------------------------------------------- marches
  /** Each march's screen geometry, in draw order (last = on top): returns first, then marches heading for a city, then the
   *  march the player tapped. A returning arrow fades while it passes close to an outgoing one. */
  private marchGeo(w: number, h: number, view: View, scene: Scene) {
    const { size } = scene;
    const geo = scene.marches.map((m) => {
      const [x0, y0] = cellCentrePx(view, w, h, size, m.from[0], m.from[1]);
      // the target the short way round from the origin
      const x1 = x0 + wrapDelta(m.from[0], m.to[0], size) * view.s;
      const y1 = y0 - wrapDelta(m.from[1], m.to[1], size) * view.s;
      const t = Math.min(1, Math.max(0, (scene.now - m.startMs) / Math.max(1, m.endMs - m.startMs)));
      const color = m.kind === "attack" ? "#E24B3C" : m.kind === "support" ? "#6FA0E8" : m.kind === "return" ? "#B8B0A0" : "#D9C84A";
      const front = !!m.id && m.id === scene.frontMarch;
      return { m, x0, y0, x1, y1, ax: x0 + (x1 - x0) * t, ay: y0 + (y1 - y0) * t, color, alpha: 1, front };
    });
    const rank = (g: (typeof geo)[number]) => (g.front ? 2 : g.m.kind === "return" ? 0 : 1);
    geo.sort((a, b) => rank(a) - rank(b));
    const outgoing = geo.filter((g) => g.m.kind !== "return");
    for (const g of geo) {
      if (g.m.kind !== "return" || g.front || !outgoing.length) continue;
      // distance to the nearest outgoing arrow; the countdown pill sits ~32px above each arrow, so the vertical gap
      // counts for a little less than the horizontal one
      const near = Math.min(...outgoing.map((o) => Math.hypot((g.ax - o.ax) * 0.75, g.ay - o.ay)));
      g.alpha = Math.min(1, Math.max(0.25, (near - 30) / 70));
    }
    return geo;
  }

  /** The march whose marker (squad or arrow) or countdown pill is under a CSS-pixel point, topmost first — from the army
   *  layer's last frame when it drew this very view (the geometry and server time the player sees), else from the
   *  marches' geometry now. Reading never advances the walk or the crowd's clearance. */
  hitMarch(w: number, h: number, view: View, scene: Scene, px: number, py: number): string | null {
    if (!scene.layers.marches) return null;
    const region = view.s >= REGION_MIN;
    const a = this.armyFrame;
    if (a && a.w === w && a.h === h && a.view.cx === view.cx && a.view.cy === view.cy && a.view.s === view.s) {
      for (let i = a.items.length - 1; i >= 0; i--) {
        const it = a.items[i];
        if (!it.id) continue;
        if (it.pill && inBox(it.pill, px, py)) return it.id;
        if (it.hit ? inBox(it.hit, px, py) : Math.hypot(px - it.ax, py - it.ay) <= (region ? 20 : 12)) return it.id;
      }
      return null;
    }
    const geo = this.marchGeo(w, h, view, scene);
    for (let i = geo.length - 1; i >= 0; i--) {
      const g = geo[i];
      if (!g.m.id) continue;
      if (Math.hypot(px - g.ax, py - g.ay) <= (region ? 20 : 12)) return g.m.id;
      // the pill: ~24px tall, 20px above the arrow, about 90px wide
      if (region && Math.abs(px - g.ax) <= 48 && py >= g.ay - 46 && py <= g.ay - 18) return g.m.id;
    }
    return null;
  }

  /** Valor Tools' guild marks (Oct 15 2026): a small pennant at the top-right of each claimed or op-targeted city — crimson for a
   *  guildmate's claim, gold for yours, a gold ring for a guild op target. Under the marches; off with the Guild marks switch. */
  private drawGuildMarks(ctx: CanvasRenderingContext2D, w: number, h: number, view: View, scene: Scene) {
    const region = view.s >= REGION_MIN;
    const colour = { claim: "#E24B3C", mine: "#DEBE70", op: "#F1C94B" } as const;
    ctx.save();
    for (const m of scene.guildMarks ?? []) {
      const [cx, cy] = cellCentrePx(view, w, h, scene.size, m.x, m.y);
      if (cx < -40 || cy < -40 || cx > w + 40 || cy > h + 40) continue;
      if (!region) {
        ctx.fillStyle = colour[m.kind];
        ctx.strokeStyle = "rgba(7,9,11,0.85)";
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(cx, cy, Math.max(2.5, view.s * 0.35), 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        continue;
      }
      if (m.kind === "op") {
        ctx.strokeStyle = colour.op;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 4]);
        ctx.beginPath(); ctx.arc(cx, cy, view.s * 0.5, 0, Math.PI * 2); ctx.stroke();
        ctx.setLineDash([]);
        continue;
      }
      // a pennant on a pole at the city's top-right corner
      const px = cx + view.s * 0.32, py = cy - view.s * 0.38, k = Math.max(0.8, Math.min(1.4, view.s / 40));
      ctx.strokeStyle = "rgba(7,9,11,0.9)";
      ctx.lineWidth = 2.5 * k;
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, py - 22 * k); ctx.stroke();
      ctx.strokeStyle = "#E8DCC0";
      ctx.lineWidth = 1.2 * k;
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(px, py - 22 * k); ctx.stroke();
      ctx.fillStyle = colour[m.kind];
      ctx.strokeStyle = "rgba(7,9,11,0.9)";
      ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(px, py - 22 * k); ctx.lineTo(px + 14 * k, py - 17 * k); ctx.lineTo(px, py - 12 * k); ctx.closePath(); ctx.fill(); ctx.stroke();
    }
    ctx.restore();
  }

  /** The base pass's share of the marches: every route's line (the dark casing under all of them first, then the coloured
   *  dashes), then the end marks — a gold dot for your marches' origin, a crimson ring for an attacker's city, the reticle
   *  on your target. The markers and countdowns are the army layer's (drawArmyLayer). */
  private drawMarchLines(ctx: CanvasRenderingContext2D, w: number, h: number, view: View, scene: Scene) {
    const region = view.s >= REGION_MIN;
    const geo = this.marchGeo(w, h, view, scene);
    ctx.save();
    ctx.lineCap = "round";
    if (region) {
      ctx.strokeStyle = "rgba(7,9,11,0.7)";
      ctx.lineWidth = 8;
      for (const g of geo) { ctx.beginPath(); ctx.moveTo(g.x0, g.y0); ctx.lineTo(g.x1, g.y1); ctx.stroke(); }
    }
    ctx.lineWidth = region ? 3 : 1.5;
    ctx.setLineDash(region ? [10, 7] : [4, 3]);
    for (const g of geo) { ctx.strokeStyle = g.color; ctx.beginPath(); ctx.moveTo(g.x0, g.y0); ctx.lineTo(g.x1, g.y1); ctx.stroke(); }
    ctx.setLineDash([]);
    for (const { m, x0, y0, x1, y1 } of geo) {
      if (m.incoming && m.kind === "attack") {
        ctx.strokeStyle = "#FF7656";
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(x0, y0, region ? view.s * 0.3 : 5, 0, Math.PI * 2); ctx.stroke();
      } else {
        ctx.fillStyle = "#DEBE70";
        ctx.beginPath(); ctx.arc(x0, y0, region ? 5 : 3, 0, Math.PI * 2); ctx.fill();
      }
      if (m.kind === "attack" && region && !m.incoming) {
        const r = view.s * 0.42;
        ctx.strokeStyle = "#FF7656";
        ctx.lineWidth = 2.2;
        ctx.fillStyle = "rgba(206,30,26,0.18)";
        ctx.beginPath(); ctx.arc(x1, y1, r, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x1, y1 - r - 10); ctx.lineTo(x1, y1 - r + 4);
        ctx.moveTo(x1, y1 + r - 4); ctx.lineTo(x1, y1 + r + 10);
        ctx.moveTo(x1 - r - 10, y1); ctx.lineTo(x1 - r + 4, y1);
        ctx.moveTo(x1 + r - 4, y1); ctx.lineTo(x1 + r + 10, y1);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // ---------------------------------------------------------------------------------------------- the army layer
  /** The sprite set for the canvas's pixel density: the 3x atlases above 2x, the 2x ones otherwise (each is the squad's
   *  largest on-screen width times its scale, so neither is ever drawn larger than its pixels). */
  private armySet(dpr: number): ArmySet {
    return ARMY.sets[dpr > 2 ? "3x" : "2x"];
  }

  /** The glow under a squad — crimson for an attack, blue for support (army.ts GLOW_COLORS): one soft ellipse per colour,
   *  made once, drawn scaled with a pulsing alpha. */
  private glow(kind: keyof typeof GLOW_COLORS): HTMLCanvasElement | null {
    const have = this.glowTex.get(kind);
    if (have) return have;
    const c = document.createElement("canvas");
    c.width = GLOW_TEX;
    c.height = GLOW_TEX / 2;
    const g = c.getContext("2d");
    if (!g) return null;
    const { core, mid, rim } = GLOW_COLORS[kind];
    g.save();
    g.scale(1, 0.5);
    const grad = g.createRadialGradient(GLOW_TEX / 2, GLOW_TEX / 2, 0, GLOW_TEX / 2, GLOW_TEX / 2, GLOW_TEX / 2);
    grad.addColorStop(0, `rgba(${core},0.95)`);
    grad.addColorStop(0.45, `rgba(${mid},0.55)`);
    grad.addColorStop(0.8, `rgba(${rim},0.14)`);
    grad.addColorStop(1, `rgba(${rim},0)`);
    g.fillStyle = grad;
    g.fillRect(0, 0, GLOW_TEX, GLOW_TEX);
    g.restore();
    this.glowTex.set(kind, c);
    return c;
  }

  /** The army layer: the marker of every march — a walking squad (glow, frame, mirrored for rightward travel) or the arrow
   *  it falls back to — and the countdown pills, over the base canvas, in the base's draw order (returns under the rest,
   *  the tapped march on top). Drawn at animation time `t` (ms, performance.now); `reduced` holds the first frame and a
   *  steady glow. It keeps what it drew (armyFrame) for hitMarch and the loop. */
  drawArmyLayer(ctx: CanvasRenderingContext2D, w: number, h: number, dpr: number, view: View, scene: Scene, t: number, reduced: boolean) {
    const t0 = performance.now();
    this.armyDraws++;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, w, h);
    const frame: ArmyFrame = { w, h, view: { ...view }, now: scene.now, items: [], squads: 0, pending: false, ms: 0 };
    if (!scene.layers.marches) {
      this.crowd.reset();
      this.armyFrame = frame;
      return;
    }
    const region = view.s >= REGION_MIN;
    const geo = this.marchGeo(w, h, view, scene);
    const size = armySize(view.s);
    const set = this.armySet(dpr);
    // 1. what each march would be: its banner, facing and intended footprint (the realm, an unknown kind and the
    // Animated armies switch off keep arrows)
    const squads = region && scene.layers.armies !== false;
    const items: ArmyItem[] = geo.map((g) => {
      const banner = squads ? bannerFor(g.m.kind) : null;
      let mirror = false;
      if (banner) {
        const face = facing(g.x1 - g.x0, g.y1 - g.y0, g.m.id ? this.facings.get(g.m.id) ?? null : null);
        if (g.m.id) this.facings.set(g.m.id, face);
        mirror = mirrored(face); // the art walks left; rightward travel flips it
      }
      return { id: g.m.id, g, banner, mirror, foot: banner ? footprint(g.ax, g.ay, size, mirror) : null, rep: "arrow", box: null, hit: null, pill: null, img: null, walk: 0, shown: false };
    });
    // 2. the crowd: every march that could be a squad, with the footprint its squad would have now
    const crowd = this.crowd.resolve(items.filter((it) => it.banner && it.id).map((it) => ({ id: it.id!, box: it.foot! })), t);
    frame.pending = crowd.pending;
    // 3. squads where the art is here and nothing overlaps; arrows elsewhere (and while the art is on its way)
    for (const it of items) {
      if (!it.banner || !it.id || crowd.arrows.has(it.id)) continue;
      const url = art.map.army(set.files[it.banner]);
      const img = this.sprites.get(url);
      if (!img) {
        if (this.sprites.failed(url) && !this.armyFailedWarned) {
          this.armyFailedWarned = true;
          console.warn("map: the army sprites didn't load; marches keep their arrows");
        }
        continue;
      }
      it.rep = "squad";
      it.img = img;
      it.box = { x: it.foot!.x + ARMY_PAD, y: it.foot!.y + ARMY_PAD, w: it.foot!.w - 2 * ARMY_PAD, h: it.foot!.h - 2 * ARMY_PAD };
      // a usable touch target: the drawn box plus a little ground under the feet (the arrow's old reach), at least 44 x 44
      // about its centre
      const hw = Math.max(44, it.box.w), hh = Math.max(44, it.box.h + 12);
      it.hit = { x: it.box.x + it.box.w / 2 - hw / 2, y: it.box.y + (it.box.h + 12) / 2 - hh / 2, w: hw, h: hh };
      // only squads on the canvas are painted (and keep the layer at its walking cadence); the rest stay squads for the
      // crowd and for hit testing, which never reaches them
      it.shown = it.box.x < w + size && it.box.x + it.box.w > -size && it.box.y < h + size && it.box.y + it.box.h > -size;
      if (it.shown) frame.squads++;
    }
    // 4. (a) the glow under every attacking (crimson) and supporting (blue) squad; a return carries none
    if (frame.squads) {
      for (const it of items) {
        if (it.rep !== "squad" || !it.shown) continue;
        const kind = glowFor(it.banner);
        const glow = kind ? this.glow(kind) : null;
        if (!glow) continue;
        const k = glowAt(t, phaseOf(it.id!), reduced);
        const gw = size * 1.35, gh = gw * 0.42;
        ctx.globalAlpha = it.g.alpha * Math.min(1, k) * 0.9;
        ctx.drawImage(glow, it.g.ax - gw / 2, it.g.ay - size * 0.1 - gh / 2, gw, gh);
      }
      ctx.globalAlpha = 1;
    }
    // (b) the markers, in draw order
    const k = size / set.union.w;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "medium";
    const sc = region ? 1 : 0.6;
    for (const it of items) {
      if (it.rep === "squad" && it.img) {
        const fi = frameAt(t, phaseOf(it.id!), reduced);
        it.walk = fi;
        if (!it.shown) continue;
        ctx.save();
        ctx.globalAlpha = it.g.alpha;
        if (it.mirror) {
          ctx.translate(it.g.ax, 0);
          ctx.scale(-1, 1);
          ctx.translate(-it.g.ax, 0);
        }
        ctx.drawImage(it.img, fi * set.cell[0], 0, set.cell[0], set.cell[1], it.g.ax - set.anchor[0] * k, it.g.ay - set.anchor[1] * k, set.cell[0] * k, set.cell[1] * k);
        ctx.restore();
      } else {
        if (it.g.ax < -40 || it.g.ax > w + 40 || it.g.ay < -40 || it.g.ay > h + 40) continue;
        paintArrow(ctx, it.g.ax, it.g.ay, Math.atan2(it.g.y1 - it.g.y0, it.g.x1 - it.g.x0), it.g.color, it.g.alpha, sc);
      }
    }
    // (c) the countdown pills above every marker, on top of everything (the region only, as before)
    if (region) {
      for (const it of items) {
        if (it.g.ax < -120 || it.g.ax > w + 120 || it.g.ay < -80 || it.g.ay > h + 120) continue;
        const top = it.rep === "squad" && it.box ? it.box.y - 8 : it.g.ay - 20;
        it.pill = paintPill(ctx, it.g.ax, top, fmtLeft(it.g.m.endMs - scene.now), it.g.m.kind, it.g.color, it.g.alpha);
      }
    }
    frame.items = items.map((it) => ({ id: it.id, ax: it.g.ax, ay: it.g.ay, rep: it.rep, mirror: it.mirror, banner: it.banner, box: it.box, hit: it.hit, pill: it.pill, walk: it.walk }));
    frame.ms = performance.now() - t0;
    this.armyFrame = frame;
  }

  // ---------------------------------------------------------------------------------------------- hit testing
  /** The city drawn under a CSS-pixel point. Region: the same layout the frame draws — the front-most sprite (castle or
   *  stockade) whose painted pixels, by its own family's mask, are under the point, else the nearest sprite or marker
   *  target (a stockade keeps its diamond's zone as a fallback). Realm: the nearest marker within a thumb's reach. */
  hitTest(w: number, h: number, view: View, scene: Scene, px: number, py: number): Cell | null {
    const { size } = scene;
    const s = view.s;
    if (s >= REGION_MIN) {
      const a = castleAlpha(s);
      const items: HitItem<Cell>[] = this.cityLayout(view, w, h, scene).map((it) => ({
        item: it.c,
        tier: it.tier,
        family: it.family,
        box: a > 0 ? it.box : null,
        // the marker's target: while the sprites fade; wherever a sprite isn't drawn (its PNG not here, or failed) and its
        // diamond stands in full; and under a stockade all the way in — it stands low (its rect starts below the diamond's
        // tip at the opening zoom), so the zone a castle's rect already holds stays its tap fallback
        marker: a < 1 || it.family === "npc" || !this.sprites.ready(spriteUrl(it)) ? { x: it.px, y: it.py, r: markerHalf(s, it.tier) } : null,
        mask: it.painted ? it.key : undefined,
      }));
      return hitCities(items, px, py, (family, tier, u, v, mask) => this.sprites.opaque(family, tier, u, v, mask));
    }
    let bestC: Cell | null = null;
    let bestD = 16;
    for (const c of scene.cityList) {
      if (!tierOf(c.level)) continue;
      const [cx, cy] = cellCentrePx(view, w, h, size, c.x, c.y);
      const dd = Math.hypot(cx - px, cy - py);
      if (dd < bestD) { bestD = dd; bestC = c; }
    }
    return bestC;
  }
}

/** Forest cover per P-cell patch of the world, and the lakes at the patch's samples, from four samples of the game's
 *  terrain per patch (one in the middle of each P/2-cell quarter), worked out once per patch and kept (one field per patch
 *  size, per terrain). Patch coordinates wrap with the world. */
export class DensityField {
  private forestRaw: Float32Array;
  /** which of the patch's four samples are lake: bit (qx + 2 qy) for the quarter qx west/east, qy south/north */
  private lakeBits: Uint8Array;
  private n: number;
  constructor(private terrain: TerrainCache, private P: number, size: number) {
    this.n = Math.max(1, Math.ceil(size / P));
    this.forestRaw = new Float32Array(this.n * this.n).fill(-1);
    this.lakeBits = new Uint8Array(this.n * this.n);
  }
  private index(px: number, py: number): number {
    const n = this.n;
    const i = ((Math.floor(px) % n) + n) % n;
    const j = ((Math.floor(py) % n) + n) % n;
    const k = j * n + i;
    if (this.forestRaw[k] < 0) {
      const P = this.P;
      const a = Math.floor(P / 4);
      const b = Math.floor((3 * P) / 4);
      let forest = 0;
      let lakes = 0;
      [[a, a], [b, a], [a, b], [b, b]].forEach(([dx, dy], bit) => {
        const t = this.terrain.sample(i * P + dx, j * P + dy);
        if (t === 1) forest++;
        else if (t === 3) lakes |= 1 << bit;
      });
      this.forestRaw[k] = forest / 4;
      this.lakeBits[k] = lakes;
    }
    return k;
  }
  /** 1 if the sample of the P/2-cell quarter (ux, uy) — quarter coordinates, two per patch each way — is lake, else 0 */
  lake(ux: number, uy: number): number {
    const px = Math.floor(ux / 2);
    const py = Math.floor(uy / 2);
    return (this.lakeBits[this.index(px, py)] >> (ux - 2 * px + 2 * (uy - 2 * py))) & 1;
  }
  /** the forest cover blurred over the 3 x 3 patches around (1 2 1 weights) */
  forestSmooth(px: number, py: number): number {
    let sum = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const wgt = (dx ? 1 : 2) * (dy ? 1 : 2);
        sum += this.forestRaw[this.index(px + dx, py + dy)] * wgt;
      }
    }
    return sum / 16;
  }
}

/** Lakes as continuous painted water (at `alpha`). Every lake cell (or, far out, every lake sample) adds a smooth round
 *  kernel to a field — metaballs: neighbouring cells merge into one body with rounded, organic shores instead of the
 *  grid's squares — with a little world-aligned wobble along the shore. The field's contour bands are painted as a damp
 *  dark shore, light shallows, the water and, inside bigger lakes, a deeper middle. The bands are computed at several
 *  samples per grid unit into a small bitmap stretched over the tile. The tile covers cells [x0, x0 + R) west to east and
 *  [top - R, top) south to north; `unit` is the grid's size in cells (1, or P/2 far out); `lake(gx, gy)` is 0–1 for that grid's unit (gx, gy). */
function paintLakes(g: CanvasRenderingContext2D, x0: number, top: number, R: number, unit: number, s: number, size: number, alpha: number, lake: (gx: number, gy: number) => number, castle?: (gx: number, gy: number) => boolean, mats?: TerrainMaterials, pw?: PaintedLake | null) {
  if (alpha < MIN_ALPHA) return;
  const M = 2; // margin, in grid units: kernels reach LAKE_KERNEL units
  const units = R / unit;
  const gx0 = Math.floor(x0 / unit) - M;
  const gyN = Math.floor(top / unit) - 1 + M; // the grid row M rows north of the tile's northmost row
  const span = units + 2 * M;
  const K = Math.max(4, Math.min(48, Math.round((unit * s) / 2.2))); // samples per grid unit (≈ 2 px close in)
  const N = units * K;
  const F = new Float32Array(N * N);
  const rK = LAKE_KERNEL * K;
  let any = false;
  for (let j = 0; j < span; j++) {
    for (let i = 0; i < span; i++) {
      const v = lake(gx0 + i, gyN - j);
      if (v <= 0) continue;
      any = true;
      // this unit's centre in sample coordinates (x from the tile's west edge, y from its north edge)
      const cx = (i - M + 0.5) * K;
      const cy = (j - M + 0.5) * K;
      const sx0 = Math.max(0, Math.floor(cx - rK));
      const sx1 = Math.min(N - 1, Math.ceil(cx + rK));
      const sy0 = Math.max(0, Math.floor(cy - rK));
      const sy1 = Math.min(N - 1, Math.ceil(cy + rK));
      for (let sy = sy0; sy <= sy1; sy++) {
        const dy = (sy + 0.5 - cy) / rK;
        for (let sx = sx0; sx <= sx1; sx++) {
          const dx = (sx + 0.5 - cx) / rK;
          const d2 = dx * dx + dy * dy;
          if (d2 < 1) F[sy * N + sx] += v * (1 - d2) * (1 - d2);
        }
      }
    }
  }
  if (!any) return;
  // castles' footprints (cell-by-cell lakes only): a soft dry patch subtracted from the field, so the shore bends round
  if (castle) {
    const rx = LAKE_CARVE_RX * K;
    const ry = LAKE_CARVE_RY * K;
    for (let j = 0; j < span; j++) {
      for (let i = 0; i < span; i++) {
        if (!castle(gx0 + i, gyN - j)) continue;
        const cx = (i - M + 0.5) * K;
        const cy = (j - M + 0.5 + LAKE_CARVE_DY) * K;
        const sx0 = Math.max(0, Math.floor(cx - rx));
        const sx1 = Math.min(N - 1, Math.ceil(cx + rx));
        const sy0 = Math.max(0, Math.floor(cy - ry));
        const sy1 = Math.min(N - 1, Math.ceil(cy + ry));
        for (let sy = sy0; sy <= sy1; sy++) {
          const dy = (sy + 0.5 - cy) / ry;
          for (let sx = sx0; sx <= sx1; sx++) {
            const dx = (sx + 0.5 - cx) / rx;
            const e2 = dx * dx + dy * dy;
            if (e2 < 1) F[sy * N + sx] -= LAKE_CARVE * (1 - e2) * (1 - e2);
          }
        }
      }
    }
  }
  // surface detail (ripples, sheen, the foam and sand lines) only where a grid unit is big enough to show it
  const detail = smooth(10, 40, unit * s);
  // the painted materials' variation (Oct 14 2026), gated by the zoom on its own so far-out water stays calm
  const tex: LakeTexture | undefined = pw
    ? { water: null, shore: null, k: 0, mx: 0, my: 0, pw }
    : mats && (mats.lakeWater || mats.lakeShore) ? { water: mats.lakeWater, shore: mats.lakeShore, k: lakeTextureStrength(s), mx: 0, my: 0 } : undefined;
  const c = document.createElement("canvas");
  c.width = N;
  c.height = N;
  const cg = c.getContext("2d");
  if (!cg) return;
  const img = cg.createImageData(N, N);
  const d = img.data;
  const n = size / unit;
  for (let j = 0; j < N; j++) {
    const wy = (top - ((j + 0.5) / K) * unit) / unit; // the sample's world y, in grid units
    for (let i = 0; i < N; i++) {
      const wx = (x0 + ((i + 0.5) / K) * unit) / unit;
      const f = F[j * N + i] <= 0.1 ? 0 : lakeWobbled(F[j * N + i], wx, wy, n);
      if (f <= 0.1) continue;
      // the material is sampled in world cells (wx, wy are grid units: × unit), so its period holds across the coarse grid
      if (tex) { tex.mx = wx * unit; tex.my = wy * unit; }
      const px = lakePixel(f, wx, wy, n, detail, tex);
      if (!px) continue;
      const [r, gr, b2, al] = px;
      const o = (j * N + i) * 4;
      d[o] = r;
      d[o + 1] = gr;
      d[o + 2] = b2;
      d[o + 3] = al * 255;
    }
  }
  cg.putImageData(img, 0, 0);
  g.save();
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = "medium";
  g.globalAlpha = alpha;
  g.drawImage(c, 0, 0, R * s, R * s);
  g.restore();
  c.width = c.height = 0;
}

/** A lake kernel's reach, in grid units. */
const LAKE_KERNEL = 1.2;
// (Oct 14 2026, natural materials: the foam, shallows and sand darker and the sheen dimmer — the pale halo and bright
// streaks were the two things the reference does without; the water a little deeper and more teal, toward its palette)
const LAKE_DEEP = [34, 58, 70] as const;
const LAKE_WATER = [58, 88, 98] as const;
const LAKE_SHALLOWS = [80, 108, 106] as const;
const LAKE_SHORE = [40, 46, 34] as const;
const LAKE_FOAM = [150, 166, 156] as const;
const LAKE_SAND = [128, 118, 88] as const;
const LAKE_SHEEN = [118, 148, 154] as const;
/** how strongly the foam line tints the edge, and the sheen the open water (were 0.7 and 0.4) */
const LAKE_FOAM_MIX = 0.42;
const LAKE_SHEEN_MIX = 0.2;
/** A castle's dry patch: an ellipse (cells) centred this far south of its cell centre (its footing), and how much field it
 *  takes away — enough that a lake beside it never reaches the castle's base. */
const LAKE_CARVE_RX = 1.0;
const LAKE_CARVE_RY = 0.85;
const LAKE_CARVE_DY = 0.12;
const LAKE_CARVE = 1.8;
/** The water field's value at the painted shore: below it is dry ground (the damp bank and sand only). */
export const LAKE_EDGE = 0.47;
/** Painted world v2's shore relief: a shore cell's chance of a patch, the patches' least spacing and their length (cells). */
const SHORE_CHANCE = 0.65;
const SHORE_SPACING = 1.15;
const SHORE_LENGTH = 1.1;
/** Chance per candidate spot (two per lake cell) of a stone. */
const STONE_CHANCE = 0.4;

/** The lake materials for one paintLakes call: the swatches, the zoom-gated strength, and the sample's world point (cells,
 *  set per sample). */
export interface LakeTexture {
  water: Material | null;
  shore: Material | null;
  k: number;
  mx: number;
  my: number;
  /** painted world v2: the painted water, shallows and gravel sampled for their own colours (not as variation) */
  pw?: PaintedLake | null;
}
/** Painted world v2's lake materials, sampled absolutely (zero means), and how much of their detail shows (far out the
 *  swatch's mean colour stands in, so a coarse lake doesn't shimmer). */
export interface PaintedLake {
  water: Material;
  shallows: Material;
  gravel: Material;
  detail: number;
}
const PW_LAKE_MEAN = { water: [15.4, 97.6, 129.2], shallows: [24.2, 122.8, 139.3], gravel: [164.5, 136.1, 76.9] } as const;
/** painted lakes: the deep water darkens toward the middle of a big lake, to this much of the swatch */
const PW_LAKE_DEEP = 0.7;
const PW_LAKE_SAT = 0.8;
const PW_LAKE_TONE = 0.86;
const PWW = [0, 0, 0];
const PWS = [0, 0, 0];
const PWG = [0, 0, 0];
const PWX = [0, 0, 0];
export const paintedLakeDetail = (s: number) => smooth(8, 30, s);

/** A painted world v2 lake sample: the painted shallows at the water's edge giving way to the painted water, darkening
 *  into the deep; a wet gravel bank outside it, damp and dark at the waterline, dry and pale toward the meadow. No foam
 *  line, no pale halo: the swatches' own ripples are the surface. [r, g, b, alpha 0–1] or null. */
export function paintedLakePixel(f: number, mx: number, my: number, p: PaintedLake): [number, number, number, number] | null {
  if (f <= 0.24) return null;
  const k = p.detail;
  // two samples of the swatch — as laid, and a quarter turn round at twice the size (an 8-cell period, so the world
  // still wraps) — averaged, so the mirrored repeat's symmetric shapes don't show on open water; the contrast the
  // averaging loses is put back
  const pick = (m: Material, mean: readonly number[], out: number[]) => {
    if (k > 0) {
      materialDelta(m, mx, my, out);
      materialDelta(m, my * 0.5 + 1.3, -mx * 0.5 + 0.7, PWX);
      for (let c = 0; c < 3; c++) out[c] = mean[c] + ((out[c] + PWX[c]) / 2 - mean[c]) * 1.15;
    }
    for (let c = 0; c < 3; c++) out[c] = mix(mean[c], k > 0 ? out[c] : mean[c], k);
    return out;
  };
  let r = 0, g = 0, b = 0, al = 0;
  const wa = smooth(0.47, 0.55, f);
  if (wa > 0) {
    const w = pick(p.water, PW_LAKE_MEAN.water, PWW);
    const sh = pick(p.shallows, PW_LAKE_MEAN.shallows, PWS);
    const edge = smooth(0.5, 0.85, f); // shallows → open water
    const deep = mix(1, PW_LAKE_DEEP, smooth(0.85, 1.9, f));
    r = mix(sh[0], w[0], edge) * deep; g = mix(sh[1], w[1], edge) * deep; b = mix(sh[2], w[2], edge) * deep;
    // toward the reference's deeper, less saturated water (its lake ~31, 77, 94)
    const lum = r * 0.3 + g * 0.55 + b * 0.15;
    r = mix(lum, r, PW_LAKE_SAT) * PW_LAKE_TONE; g = mix(lum, g, PW_LAKE_SAT) * PW_LAKE_TONE; b = mix(lum, b, PW_LAKE_SAT) * PW_LAKE_TONE;
    al = wa;
    r *= al; g *= al; b *= al;
  }
  // the gravel bank under the water's edge and out onto the meadow, wet (darker) toward the water
  const bank = smooth(0.27, 0.37, f) * 0.9 * (1 - al);
  if (bank > 0) {
    const gr = pick(p.gravel, PW_LAKE_MEAN.gravel, PWG);
    const wet = mix(0.82, 0.58, smooth(0.36, 0.5, f));
    r += gr[0] * wet * bank; g += gr[1] * wet * bank; b += gr[2] * wet * bank; al += bank;
  }
  if (al <= 0) return null;
  return [r / al, g / al, b / al, al];
}
/** How much of each material's variation reaches the colour, at full strength. */
const LAKE_WATER_TEX = 1.0;
const LAKE_SHORE_TEX = 1.0;
/** The materials fade in with the zoom (calm, flat water far out): none below the cell-by-cell lakes. */
export const lakeTextureStrength = (s: number) => smooth(14, 56, s);
const ZERO3: readonly number[] = [0, 0, 0];
const DW = [0, 0, 0];
const DS = [0, 0, 0];

/** The field plus the shore's world-aligned wobble. */
function lakeWobbled(f: number, wx: number, wy: number, n: number): number {
  const fc = Math.min(1, f);
  return f + (lakeWobble(wx, wy, n, 0.55, 977) - 0.5) * 0.34 * 4 * fc * (1 - fc) + (lakeWobble(wx, wy, n, 1.4, 983) - 0.5) * 0.14 * 4 * fc * (1 - fc);
}

/** One sample of a lake: [r, g, b, alpha 0–1] or null. The bands from the land in: a damp dark bank, a line of pale wet
 *  sand, the foam line at the water's edge, the light shallows, then the water darkening steadily into the deep — with
 *  soft ripples and long light streaks of sheen on the open water (Oct 14 2026). */
export function lakePixel(f: number, wx: number, wy: number, n: number, detail: number, tex?: LakeTexture): [number, number, number, number] | null {
  if (tex?.pw) return paintedLakePixel(f, tex.mx, tex.my, tex.pw);
  // the materials' variation at this point (zero when there are none, or far out)
  const tk = tex ? tex.k : 0;
  const dw = tk > 0 && tex!.water && f > 0.44 ? materialDelta(tex!.water, tex!.mx, tex!.my, DW) : ZERO3;
  const ds = tk > 0 && tex!.shore && f < 0.5 ? materialDelta(tex!.shore, tex!.mx, tex!.my, DS) : ZERO3;
  const kw = tk * LAKE_WATER_TEX;
  const ks = tk * LAKE_SHORE_TEX;
  // the water: a colour that deepens with the field (the middle of a big lake is darkest)
  const wa = smooth(0.58, 0.68, f);
  let r = 0, gr = 0, b = 0, al = 0;
  if (wa > 0) {
    const depth = smooth(0.7, 1.75, f);
    let wr = mix(LAKE_WATER[0], LAKE_DEEP[0], depth) + dw[0] * kw;
    let wg = mix(LAKE_WATER[1], LAKE_DEEP[1], depth) + dw[1] * kw;
    let wb = mix(LAKE_WATER[2], LAKE_DEEP[2], depth) + dw[2] * kw;
    if (detail > 0) {
      // ripples: a fine, soft unevenness; sheen: short wavy east–west crests catching the light, in loose patches on the
      // open water
      const rip = (lakeWobble(wx, wy, n, 4, 991) - 0.5) * 12 * detail;
      const phase = wy * 5.5 + lakeWobble(wx, wy, n, 1.1, 993) * 3.2 + (lakeWobble(wx, wy, n, 3, 995) - 0.5) * 0.6;
      const crest = smooth(0.55, 0.95, Math.sin(phase * Math.PI * 2));
      const dash = smooth(0.42, 0.72, lakeNoise(wx, wy, n, 2.2, 4.5, 997));
      const patch = smooth(0.35, 0.75, lakeWobble(wx, wy, n, 0.45, 999));
      const sheen = crest * dash * (0.35 + 0.65 * patch) * smooth(0.6, 0.85, f) * LAKE_SHEEN_MIX * detail;
      wr = mix(wr + rip, LAKE_SHEEN[0], sheen);
      wg = mix(wg + rip, LAKE_SHEEN[1], sheen);
      wb = mix(wb + rip, LAKE_SHEEN[2], sheen);
    }
    r = wr * wa; gr = wg * wa; b = wb * wa; al = wa;
  }
  // the shallows under the edge of the water
  let k = smooth(0.46, 0.52, f) * (1 - al);
  r += (LAKE_SHALLOWS[0] + dw[0] * kw) * k; gr += (LAKE_SHALLOWS[1] + dw[1] * kw) * k; b += (LAKE_SHALLOWS[2] + dw[2] * kw) * k; al += k;
  // the foam line, over the shallows (a thin pale band at the water's edge)
  const foam = smooth(0.44, 0.48, f) * (1 - smooth(0.5, 0.57, f)) * LAKE_FOAM_MIX * detail;
  if (foam > 0 && al > 0) {
    r = mix(r / al, LAKE_FOAM[0], foam) * al; gr = mix(gr / al, LAKE_FOAM[1], foam) * al; b = mix(b / al, LAKE_FOAM[2], foam) * al;
  }
  // the pale wet sand just outside it, then the damp dark bank
  k = smooth(0.36, 0.43, f) * 0.62 * detail * (1 - al);
  r += (LAKE_SAND[0] + ds[0] * ks) * k; gr += (LAKE_SAND[1] + ds[1] * ks) * k; b += (LAKE_SAND[2] + ds[2] * ks) * k; al += k;
  k = smooth(0.27, 0.38, f) * 0.5 * (1 - al);
  r += (LAKE_SHORE[0] + ds[0] * ks * 0.6) * k; gr += (LAKE_SHORE[1] + ds[1] * ks * 0.6) * k; b += (LAKE_SHORE[2] + ds[2] * ks * 0.6) * k; al += k;
  if (al <= 0) return null;
  return [r / al, gr / al, b / al, al];
}

/** The painted water field at one point (cells: x east, y north) for the cell-by-cell lakes — the same kernels, castle
 *  patches and wobble paintLakes sums, so a tree or boulder can be kept off the water it would stand in. */
export function lakeFieldAt(x: number, y: number, size: number, lake: (gx: number, gy: number) => number, castle: (gx: number, gy: number) => boolean): number {
  let f = 0;
  const cx0 = Math.floor(x);
  const cy0 = Math.floor(y);
  for (let gy = cy0 - 2; gy <= cy0 + 2; gy++) {
    for (let gx = cx0 - 2; gx <= cx0 + 2; gx++) {
      const v = lake(gx, gy);
      if (v > 0) {
        const dx = (x - (gx + 0.5)) / LAKE_KERNEL;
        const dy = (y - (gy + 0.5)) / LAKE_KERNEL;
        const d2 = dx * dx + dy * dy;
        if (d2 < 1) f += v * (1 - d2) * (1 - d2);
      }
    }
  }
  if (f <= 0) return 0;
  for (let gy = cy0 - 1; gy <= cy0 + 1; gy++) {
    for (let gx = cx0 - 1; gx <= cx0 + 1; gx++) {
      if (!castle(gx, gy)) continue;
      const dx = (x - (gx + 0.5)) / LAKE_CARVE_RX;
      const dy = (y - (gy + 0.5 - LAKE_CARVE_DY)) / LAKE_CARVE_RY;
      const e2 = dx * dx + dy * dy;
      if (e2 < 1) f -= LAKE_CARVE * (1 - e2) * (1 - e2);
    }
  }
  if (f <= 0.1) return 0;
  return lakeWobbled(f, x, y, size);
}

/** A field test for whatever stands on a cell: the piece's foot (cell fractions from the north-west corner) and its half
 *  width (cells). */
type Wet = ((fx: number, fy: number) => number) | null;
/** Does a piece standing at (fx, fy) with this half width have a foot in the painted water (or its foam line)? */
function onWater(wet: (fx: number, fy: number) => number, fx: number, fy: number, half: number): boolean {
  const edge = LAKE_EDGE - 0.08; // a little dry bank between a trunk and the foam
  return wet(fx, fy) > edge || wet(fx - half * 0.8, fy) > edge || wet(fx + half * 0.8, fy) > edge || wet(fx, fy - 0.12 * SCENERY_SCALE) > edge;
}

function cityFootprintAt(x: number, y: number, city: (x: number, y: number) => boolean): boolean {
  for (let gy = Math.floor(y) - 2; gy <= Math.floor(y) + 2; gy++) {
    for (let gx = Math.floor(x) - 2; gx <= Math.floor(x) + 2; gx++) {
      if (!city(gx, gy)) continue;
      const dx = (x - gx - 0.5) / (LAKE_CARVE_RX + 0.08);
      const dy = (y - gy - 0.5 + LAKE_CARVE_DY) / (LAKE_CARVE_RY + 0.08);
      if (dx * dx + dy * dy < 1) return true;
    }
  }
  return false;
}

function bridgeCovers(bridges: readonly Bridge[], x: number, y: number, half = 0): boolean {
  return bridges.some(b => {
    const dx = x - b.x, dy = y - b.y;
    return Math.abs(dx * b.ux + dy * b.uy) < b.halfLength + half + 0.07 &&
      Math.abs(-dx * b.uy + dy * b.ux) < b.halfWidth + half + 0.07;
  });
}

/** Is a lake cell within two cells (a kernel reaches 1.2 cells; wobble and merging a little further)? */
function nearLake(terrain: TerrainCache, wx: number, wy: number): boolean {
  for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) if (terrain.at(wx + dx, wy + dy) === 3) return true;
  return false;
}

/** Painted world v2 (Oct 15 2026): single painted trees on a forest cell — a dense forest's cell 2–3, a mid one 1–2, an
 *  edge cell mostly bare. With ~1.15 crowns a sprite that is about 45% fewer crowns than the clusters drew (2.7 crowns a
 *  cluster), and every crown smaller. `h` is the cell's own hash. */
export function paintedTrees(around: number, h: number): number {
  if (around >= 7) return h < 0.5 ? 3 : 2;
  if (around >= 4) return h < 0.5 ? 2 : 1;
  return h < 0.42 ? 1 : 0;
}
/** Which painted tree (trees.png, row-major: pine, small pine, two pines, broadleaf): pines most often. */
export function paintedTreeKind(h: number): number {
  const i = h < 0.4 ? 0 : h < 0.72 ? 1 : h < 0.84 ? 2 : 3;
  return Math.min(TREE_COUNT - 1, i);
}

/** Painted tree clusters on a forest cell (Oct 14 2026: about half the old 2–4): a dense forest's cell 2, a mid one 1–2,
 *  an edge cell mostly bare. `h` is the cell's own hash. */
export function forestClusters(around: number, h: number): number {
  if (around >= 7) return 2;
  if (around >= 4) return h < 0.5 ? 2 : 1;
  return h < 0.4 ? 1 : 0;
}

/** A small stone breaking the water: a pale ripple ring, the wet shadow, the rock lit from the north-west. */
function paintStone(g: CanvasRenderingContext2D, x: number, y: number, r: number, s: number) {
  g.lineWidth = Math.max(0.8, s * 0.008);
  g.strokeStyle = "rgba(214,226,220,0.42)";
  g.beginPath();
  g.ellipse(x, y + r * 0.25, r * 1.9, r * 0.8, 0, 0, Math.PI * 2);
  g.stroke();
  g.fillStyle = "rgba(24,36,40,0.35)";
  g.beginPath();
  g.ellipse(x + r * 0.15, y + r * 0.3, r * 1.15, r * 0.5, 0, 0, Math.PI * 2);
  g.fill();
  const grad = g.createRadialGradient(x - r * 0.35, y - r * 0.4, r * 0.1, x, y, r * 1.1);
  grad.addColorStop(0, "rgb(170,164,146)");
  grad.addColorStop(0.55, "rgb(112,108,96)");
  grad.addColorStop(1, "rgb(62,62,58)");
  g.fillStyle = grad;
  g.beginPath();
  g.ellipse(x, y, r, r * 0.68, 0, Math.PI, 0);
  g.ellipse(x, y, r, r * 0.3, 0, 0, Math.PI);
  g.fill();
}

/** Smooth periodic value noise with separate x / y frequencies (noise cells per grid unit), for the water's streaks. */
function lakeNoise(x: number, y: number, n: number, fqx: number, fqy: number, salt: number): number {
  const px = Math.max(1, Math.round(n * fqx));
  const py = Math.max(1, Math.round(n * fqy));
  const fx = (x * px) / n;
  const fy = (y * py) / n;
  const bx = Math.floor(fx);
  const by = Math.floor(fy);
  const tx = smooth(0, 1, fx - bx);
  const ty = smooth(0, 1, fy - by);
  const hh = (i: number, j: number) => cellHash(((i % px) + px) % px, ((j % py) + py) % py, salt);
  return mix(mix(hh(bx, by), hh(bx + 1, by), tx), mix(hh(bx, by + 1), hh(bx + 1, by + 1), tx), ty);
}

/** Smooth value noise in grid units, periodic over the world (exactly `n` units), about `freq` noise cells per unit. */
function lakeWobble(x: number, y: number, n: number, freq: number, salt: number): number {
  const per = Math.max(1, Math.round(n * freq)); // a whole number of noise cells round the world
  const fx = (x * per) / n;
  const fy = (y * per) / n;
  const bx = Math.floor(fx);
  const by = Math.floor(fy);
  const tx = smooth(0, 1, fx - bx);
  const ty = smooth(0, 1, fy - by);
  const hh = (i: number, j: number) => cellHash(((i % per) + per) % per, ((j % per) + per) % per, salt);
  return mix(mix(hh(bx, by), hh(bx + 1, by), tx), mix(hh(bx, by + 1), hh(bx + 1, by + 1), tx), ty);
}

/** A distant city's highlight edge: the selected city's ivory (with its glow), or the colour of one of its owner's cities. */
interface Mark { edge: string }
/** The selected city: the ivory edge, and the ivory glow (a canvas shadow) around the whole marker. */
const SELECTED_MARK: Mark = { edge: SELECT_IVORY };
const ownerMarks = new Map<string, Mark>();
/** One of the selected city's owner's other cities: an edge in its marker's own colour — so only the selected city wears an
 *  ivory edge, even where a marker's colour is pale (unaligned, or a pale realm palette colour). One per colour. */
function ownerMark(color: string): Mark {
  let m = ownerMarks.get(color);
  if (!m) {
    m = { edge: color };
    ownerMarks.set(color, m);
  }
  return m;
}

/** A soft round glow in one colour: a radial gradient on a small canvas (MapRenderer keeps one per colour), drawn under each
 *  of an owner's lit markers — a drawImage, where a canvas shadow per marker would blur a fresh surface each time. Null
 *  when the colour isn't "#rrggbb" or no canvas can be had. */
const HALO_PX = 64;
function makeHalo(color: string): HTMLCanvasElement | null {
  const m = /^#([0-9a-f]{6})$/i.exec(color);
  if (!m || typeof document === "undefined") return null;
  const n = parseInt(m[1], 16);
  const rgb = `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
  const c = document.createElement("canvas");
  c.width = c.height = HALO_PX;
  const g = c.getContext("2d");
  if (!g) {
    c.width = c.height = 0;
    return null;
  }
  const r = HALO_PX / 2;
  const grad = g.createRadialGradient(r, r, 0, r, r, r);
  grad.addColorStop(0, `rgba(${rgb},0.9)`);
  grad.addColorStop(0.45, `rgba(${rgb},0.55)`);
  grad.addColorStop(0.75, `rgba(${rgb},0.18)`);
  grad.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, HALO_PX, HALO_PX);
  return c;
}

/** The soft halo of one of an owner's lit markers, drawn before the marker, around its diamond — or its point, far out (pm:
 *  the share of the point, as paintMarker's). It reaches beyond the marker by 1.2 × the diamond's half-size, 4–9 px: as far
 *  as the selection's glow where the diamonds are full size, less far out, where markers crowd and shrink, so a halo
 *  never outshines the selected city's ivory or spreads over the next row. */
function paintHalo(ctx: CanvasRenderingContext2D, halo: HTMLCanvasElement | null, x: number, y: number, half: number, pointR: number, pm: number, alpha: number) {
  if (!halo || alpha <= 0) return;
  const r = half + (pointR - half) * pm + clamp(half * 1.2, 4, 9);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.drawImage(halo, x - r, y - r, 2 * r, 2 * r);
  ctx.restore();
}

/** A distant city: a diamond with a soft dark drop (the reference's _drawMarker) that becomes a point far out; a selected
 *  city gets the ivory edge and glow (a canvas shadow around the whole marker, as before), one of its owner's other cities
 *  an edge in its own colour over the halo paintHalo drew under it (`mark`). */
function paintMarker(ctx: CanvasRenderingContext2D, x: number, y: number, half: number, color: string, alpha: number, pm: number, pointR: number, pointA: number, mark: Mark | null, dpr: number) {
  if (alpha <= 0) return;
  ctx.save();
  if (mark === SELECTED_MARK) {
    ctx.shadowColor = SELECT_IVORY;
    ctx.shadowBlur = 8 * dpr;
  }
  if (mark) {
    ctx.strokeStyle = mark.edge;
    ctx.lineWidth = 1.2;
  }
  if (pm < 1) {
    ctx.globalAlpha = alpha * (1 - pm);
    ctx.fillStyle = "rgba(13,24,19,.45)";
    ctx.beginPath(); ctx.moveTo(x, y - half + 1); ctx.lineTo(x + half, y + 1); ctx.lineTo(x, y + half + 1); ctx.lineTo(x - half, y + 1); ctx.closePath(); ctx.fill();
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.moveTo(x, y - half); ctx.lineTo(x + half, y); ctx.lineTo(x, y + half); ctx.lineTo(x - half, y); ctx.closePath(); ctx.fill();
    if (mark) ctx.stroke();
  }
  if (pm > 0) {
    ctx.globalAlpha = alpha * pm * pointA;
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(x, y, pointR, 0, Math.PI * 2); ctx.fill();
    if (mark) ctx.stroke();
  }
  ctx.restore();
}

/** the glow texture's width (px; its height is half) */
const GLOW_TEX = 256;

/** What the army layer drew last: its view, the server time it used, and each marker's representation and boxes. */
interface ArmyFrame {
  w: number;
  h: number;
  view: View;
  now: number;
  items: { id: string | undefined; ax: number; ay: number; rep: "squad" | "arrow"; mirror: boolean; banner: Banner | null; box: ArmyBox | null; hit: ArmyBox | null; pill: ArmyBox | null; walk: number }[];
  squads: number;
  pending: boolean;
  ms: number;
}
interface ArmyItem {
  id: string | undefined;
  g: { m: March; x0: number; y0: number; x1: number; y1: number; ax: number; ay: number; color: string; alpha: number; front: boolean };
  banner: Banner | null;
  mirror: boolean;
  /** the squad's intended, padded footprint (null: never a squad) */
  foot: ArmyBox | null;
  rep: "squad" | "arrow";
  /** the drawn (unpadded) box, the touch target and the pill, once drawn */
  box: ArmyBox | null;
  hit: ArmyBox | null;
  pill: ArmyBox | null;
  img: HTMLImageElement | null;
  walk: number;
  /** a squad whose box is on the canvas (painted; the others are kept for the crowd and hit testing only) */
  shown: boolean;
}

const inBox = (b: ArmyBox, px: number, py: number) => px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h;

/** The arrowhead at a march's position, pointing along its route, in its kind's colour (the fallback marker; as it always
 *  was). */
function paintArrow(ctx: CanvasRenderingContext2D, ax: number, ay: number, angle: number, color: string, alpha: number, sc: number) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = "rgba(7,9,11,0.9)";
  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.translate(ax, ay);
  ctx.rotate(angle);
  ctx.beginPath();
  ctx.moveTo(-14 * sc, -11 * sc); ctx.lineTo(11 * sc, 0); ctx.lineTo(-14 * sc, 11 * sc); ctx.lineTo(-7 * sc, 0); ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill(); ctx.stroke();
  ctx.restore();
}

/** The countdown pill, centred on `ax` with its bottom edge at `bottom`: the swords glyph and the time left. Returns its box. */
function paintPill(ctx: CanvasRenderingContext2D, ax: number, bottom: number, label: string, kind: March["kind"], color: string, alpha: number): ArmyBox {
  const type = mapType();
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = `600 ${13 * type.tz}px ${type.text}`;
  const tw = ctx.measureText(label).width;
  const pw = tw + 34;
  const ph = Math.round(Math.max(24, 13 * type.tz + 11));
  const px = ax - pw / 2;
  const py = bottom - ph;
  ctx.fillStyle = "rgba(11,14,17,0.94)";
  ctx.strokeStyle = "rgba(201,162,74,0.9)";
  ctx.lineWidth = 1.2;
  roundRect(ctx, px, py, pw, ph, 12);
  ctx.fill(); ctx.stroke();
  // swords glyph: two crossed strokes
  ctx.strokeStyle = kind === "attack" ? "#FF7656" : color;
  ctx.lineWidth = 1.6;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(px + 9, py + 7); ctx.lineTo(px + 19, py + 17);
  ctx.moveTo(px + 19, py + 7); ctx.lineTo(px + 9, py + 17);
  ctx.stroke();
  ctx.fillStyle = "#F3E9D2";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(label, px + 26, py + ph / 2 + 0.5);
  ctx.restore();
  return { x: px, y: py, w: pw, h: ph };
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
}

// ------------------------------------------------------------------------------------------------ ravines
/** A ravine segment between two points (cells: x east, y north). A joined gorge's segments are pieces of a gently bowed
 *  link between two ravine cells: t0–t1 is the piece's span along its link, len the link's length, and freeA / freeB say
 *  whether the link's ends are loose (a gorge's end, which tapers to nothing). */
export interface RavineSeg { ax: number; ay: number; bx: number; by: number; t0: number; t1: number; len: number; freeA: boolean; freeB: boolean }
/** A ravine cell's node: its centre with a small fixed jitter, so a joined gorge winds instead of running ruler-straight. */
function ravineNode(x: number, y: number, size: number): [number, number] {
  const wx = wrap(x, size);
  const wy = wrap(y, size);
  return [x + 0.5 + (cellHash(wx, wy, 310) - 0.5) * 0.34, y + 0.5 + (cellHash(wx, wy, 311) - 0.5) * 0.34];
}

const RAVINE_STEPS: [number, number][] = [[1, 0], [0, 1], [1, 1], [-1, 1]];

/** The gorge's segments for the cells [x0, x1] x [y0, y1] (unwrapped; coordinates wrap). The game joins a ravine cell to its
 *  ravine neighbours (MapChunkScript's ravine tiles); so does this: orthogonal neighbours always, diagonal ones unless an
 *  orthogonal cell already turns that corner. The noise band that makes ravines is thin, so a cell with no neighbour is
 *  joined to the nearest ravine cell two steps away (if the cell between is open ground), and only a cell with none of
 *  those gets a short fissure of its own. Each segment belongs to one cell, so neighbouring tiles agree. */
export function ravineSegments(x0: number, y0: number, x1: number, y1: number, size: number, isRavine: (x: number, y: number) => boolean, blocked: (x: number, y: number) => boolean): RavineSeg[] {
  const out: { ax: number; ay: number; bx: number; by: number; h: number; stub?: boolean }[] = [];
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (!isRavine(x, y)) continue;
      const [ax, ay] = ravineNode(x, y, size);
      for (const [dx, dy] of RAVINE_STEPS) {
        if (!isRavine(x + dx, y + dy)) continue;
        if (dx && dy && (isRavine(x + dx, y) || isRavine(x, y + dy))) continue; // the corner is already turned
        const [bx, by] = ravineNode(x + dx, y + dy, size);
        out.push({ ax, ay, bx, by, h: cellHash(wrap(x, size), wrap(y, size), 330 + dx * 3 + dy) });
      }
      let alone = true;
      for (let dy = -1; dy <= 1 && alone; dy++) for (let dx = -1; dx <= 1; dx++) if ((dx || dy) && isRavine(x + dx, y + dy)) { alone = false; break; }
      if (!alone) continue;
      // two steps away: the nearest (ties broken in a fixed order); joined from the lower cell only, so it's drawn once
      let best: [number, number] | null = null;
      let bestD = Infinity;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== 2 || !isRavine(x + dx, y + dy)) continue;
          if (blocked(x + Math.round(dx / 2), y + Math.round(dy / 2))) continue;
          const d = dx * dx + dy * dy;
          if (d < bestD) { bestD = d; best = [dx, dy]; }
        }
      }
      if (best) {
        const [dx, dy] = best;
        // the other end is alone too and would pick this cell back: only one of the pair draws it
        const otherAlone = (() => { for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) if ((i || j) && isRavine(x + dx + i, y + dy + j)) return false; return true; })();
        if (!otherAlone || dy > 0 || (dy === 0 && dx > 0)) {
          const [bx, by] = ravineNode(x + dx, y + dy, size);
          out.push({ ax, ay, bx, by, h: cellHash(wrap(x, size), wrap(y, size), 340) });
        }
        continue;
      }
      const a = cellHash(wrap(x, size), wrap(y, size), 300) * Math.PI;
      const L = 0.36;
      out.push({ ax: ax - Math.cos(a) * L, ay: ay - Math.sin(a) * L, bx: ax + Math.cos(a) * L, by: ay + Math.sin(a) * L, h: cellHash(wrap(x, size), wrap(y, size), 350), stub: true });
    }
  }
  // how many links meet at each node: a node with one is a gorge's loose end
  const deg = new Map<string, number>();
  const nk = (x: number, y: number) => `${x.toFixed(4)},${y.toFixed(4)}`;
  for (const l of out) {
    if (l.stub) continue;
    deg.set(nk(l.ax, l.ay), (deg.get(nk(l.ax, l.ay)) ?? 0) + 1);
    deg.set(nk(l.bx, l.by), (deg.get(nk(l.bx, l.by)) ?? 0) + 1);
  }
  // each link bowed a little to one side (and a touch of S-curve), cut into pieces; its ends stay on the nodes, so joined
  // links meet exactly
  const segs: RavineSeg[] = [];
  const P = 5;
  for (const l of out) {
    const vx = l.bx - l.ax, vy = l.by - l.ay;
    const len = Math.hypot(vx, vy) || 1e-6;
    const nx = -vy / len, ny = vx / len;
    const bow = (l.h - 0.5) * 0.42 * len;
    const s2 = (cellHash(Math.round(l.ax * 97), Math.round(l.ay * 89), 351) - 0.5) * 0.14 * len;
    const at = (t: number): [number, number] => {
      const o = bow * Math.sin(Math.PI * t) + s2 * Math.sin(2 * Math.PI * t);
      return [l.ax + vx * t + nx * o, l.ay + vy * t + ny * o];
    };
    const freeA = !!l.stub || (deg.get(nk(l.ax, l.ay)) ?? 0) <= 1;
    const freeB = !!l.stub || (deg.get(nk(l.bx, l.by)) ?? 0) <= 1;
    let [px, py] = at(0);
    for (let k = 1; k <= P; k++) {
      const [qx, qy] = at(k / P);
      segs.push({ ax: px, ay: py, bx: qx, by: qy, t0: (k - 1) / P, t1: k / P, len, freeA, freeB });
      px = qx; py = qy;
    }
  }
  return segs;
}

const RAVINE_FLOOR = [34, 25, 17] as const;
const RAVINE_WALL = [86, 66, 45] as const;
const RAVINE_LIT = [196, 176, 132] as const;
const RAVINE_DUST = [150, 134, 98] as const;
const RAVINE_RIM = [222, 204, 158] as const;
/** The gorge's half widths (cells): the dark floor, the walls, the dusty lip. */
const RAVINE_WIDTH_SCALE = 1.25;
const RAVINE_FLOOR_W = 0.035 * RAVINE_WIDTH_SCALE;
const RAVINE_WALL_W = 0.12 * RAVINE_WIDTH_SCALE;
const RAVINE_LIP_W = 0.2 * RAVINE_WIDTH_SCALE;
/** Painted world v2 (Oct 15 2026): the gorges wider still, so the painted walls read — the lip's full width 0.64 cells
 *  on average (0.40–0.78 as it breathes; the brief's 0.45–0.65, wider now and then). */
const PW_RAVINE_FLOOR_W = 0.075;
const PW_RAVINE_WALL_W = 0.25;
const PW_RAVINE_LIP_W = 0.32;
/** The gorge's half widths in use: the floor, the walls, the lip (cells, before the breathing width factor). */
export interface RavineWidths { floor: number; wall: number; lip: number }
export const RAVINE_W: RavineWidths = { floor: RAVINE_FLOOR_W, wall: RAVINE_WALL_W, lip: RAVINE_LIP_W };
export const PW_RAVINE_W: RavineWidths = { floor: PW_RAVINE_FLOOR_W, wall: PW_RAVINE_WALL_W, lip: PW_RAVINE_LIP_W };

/** Painted world v2's gorge: its painted lit and shaded rock, floor, and the wall-relief patches. */
export interface PaintedRavine {
  lit: Material;
  shadow: Material;
  floor: Material;
  walls: readonly (HTMLImageElement | null)[];
}
const PRL = [0, 0, 0];
const PRS = [0, 0, 0];
const PRF = [0, 0, 0];

/** A painted gorge sample from its normalised distance `d`, how much it faces away from the light `nw` (0–1) and the
 *  three painted rocks' colours at the point: the dark floor; the walls in the painted lit rock (the south-east wall,
 *  facing the north-west light) or shaded rock (the north-west wall), lighter toward the top; a pale lip where the light
 *  catches the shaded side's edge; a soft shadow onto the meadow. Written into `out`. */
export function paintedRavinePixel(d: number, nw: number, lit: readonly number[], shade: readonly number[], floor: readonly number[], rw: RavineWidths, out: [number, number, number, number]): [number, number, number, number] {
  let r = 0, gg = 0, b = 0, al = 0;
  const over = (c0: number, c1: number, c2: number, k: number) => {
    k *= 1 - al;
    r += c0 * k; gg += c1 * k; b += c2 * k; al += k;
  };
  over(floor[0] * 0.8, floor[1] * 0.8, floor[2] * 0.8, 1 - smooth(rw.floor, rw.floor * 1.5, d));
  const up = smooth(rw.floor, rw.wall, d);
  const light = 0.72 + 0.38 * up;
  over(
    mix(lit[0], shade[0] * 0.8, nw) * light,
    mix(lit[1], shade[1] * 0.8, nw) * light,
    mix(lit[2], shade[2] * 0.8, nw) * light,
    1 - smooth(rw.wall, rw.wall * 1.1, d),
  );
  over(lit[0] * 1.12, lit[1] * 1.1, lit[2] * 1.02, (0.35 + 0.45 * nw) * smooth(rw.wall * 0.9, rw.wall, d) * (1 - smooth(rw.wall * 1.06, rw.wall * 1.22, d)));
  // the gorge's own shadow on the meadow round its lip (stronger on the lit, south-east side the walls overhang)
  over(46, 40, 26, 0.24 * (1 - smooth(rw.wall, rw.lip * 1.15, d)));
  if (al <= 0) { out[0] = out[1] = out[2] = out[3] = 0; return out; }
  out[0] = r / al; out[1] = gg / al; out[2] = b / al; out[3] = al;
  return out;
}

/** The painter's world-space distance and taper, also used to find solid bridge landings. */
function ravineFieldDistance(segments: readonly RavineSeg[], x: number, y: number, size: number): number {
  let distance = Infinity, taper = 1;
  for (const sg of segments) {
    const vx = sg.bx - sg.ax, vy = sg.by - sg.ay;
    const t = clamp(((x - sg.ax) * vx + (y - sg.ay) * vy) / (vx * vx + vy * vy || 1e-6), 0, 1);
    const d = Math.hypot(x - sg.ax - vx * t, y - sg.ay - vy * t);
    if (d >= distance) continue;
    distance = d;
    const tl = sg.t0 + (sg.t1 - sg.t0) * t;
    const end = Math.min(sg.freeA ? tl * sg.len : 9, sg.freeB ? (1 - tl) * sg.len : 9);
    taper = 0.3 + 0.7 * smooth(0, Math.min(0.4, sg.len * 0.4), end);
  }
  const width = (0.62 + 0.6 * lakeWobble(x, y, size, 0.7, 1201)) * taper;
  return distance / width + (lakeNoise(x, y, size, 7, 7, 1207) - 0.5) * 0.04 + (lakeNoise(x, y, size, 19, 19, 1209) - 0.5) * 0.018;
}

/** How much of the rock / floor materials' variation reaches the gorge, by zoom: from the castles' scale in, full close in. */
export const ravineTextureStrength = (s: number) => smooth(22, 64, s);
/** At full strength: the rock's variation on the walls (the procedural grit gives way to it), the floor's on the floor. */
const RAVINE_ROCK_TEX = 1.4;
const RAVINE_FLOOR_TEX = 1.0;
const RK = [0, 0, 0];
const FL = [0, 0, 0];
const RV: [number, number, number, number] = [0, 0, 0, 0];

/** One gorge sample from its normalised distance `d` (the wobbled half width is 1), how much it faces away from the light
 *  `nw` (0–1), the procedural grit, and the materials' variation (null: none) at strength `tk`: [r, g, b, alpha 0–1].
 *  The materials change only the colours; the alpha is the bands' alone. Written into `out`. */
export function ravinePixel(d: number, nw: number, grit: number, rock: number[] | null, floor: number[] | null, tk: number, out: [number, number, number, number]): [number, number, number, number] {
  let r = 0, gg = 0, b = 0, al = 0;
  const over = (c0: number, c1: number, c2: number, k: number) => {
    k *= 1 - al;
    r += c0 * k; gg += c1 * k; b += c2 * k; al += k;
  };
  // the floor, deep in shadow (the painted floor's grit over it)
  const kf = floor ? tk * RAVINE_FLOOR_TEX : 0;
  over(RAVINE_FLOOR[0] + (floor ? floor[0] * kf : 0), RAVINE_FLOOR[1] + (floor ? floor[1] * kf : 0), RAVINE_FLOOR[2] + (floor ? floor[2] * kf : 0), 1 - smooth(RAVINE_FLOOR_W, RAVINE_FLOOR_W * 1.6, d));
  // the walls, rock over both: the lit one warm and bright toward its top, the shaded one dark. The painted rock is
  // neutrally lit, so the light stays the bands' own: its variation is scaled by how lit the wall is.
  const up = smooth(RAVINE_FLOOR_W, RAVINE_WALL_W, d);
  const wl = (1 - nw) * (0.3 + 0.7 * up);
  const shade = mix(0.75, 0.5, nw);
  const kr = rock ? tk * RAVINE_ROCK_TEX * mix(0.55, 1, wl) : 0;
  const gk = 1 - 0.65 * (rock ? tk : 0); // the procedural grit gives way to the painted rock
  over(
    mix(RAVINE_WALL[0] * shade, RAVINE_LIT[0], wl * 0.85) + grit * gk + (rock ? rock[0] * kr : 0),
    mix(RAVINE_WALL[1] * shade, RAVINE_LIT[1], wl * 0.85) + grit * gk + (rock ? rock[1] * kr : 0),
    mix(RAVINE_WALL[2] * shade, RAVINE_LIT[2], wl * 0.85) + grit * gk * 0.8 + (rock ? rock[2] * kr : 0),
    1 - smooth(RAVINE_WALL_W, RAVINE_WALL_W * 1.15, d),
  );
  // the shaded side's lip: a thin bright edge where the light catches it (a little of the rock in it)
  const kl = rock ? tk * 0.5 : 0;
  over(RAVINE_RIM[0] + (rock ? rock[0] * kl : 0), RAVINE_RIM[1] + (rock ? rock[1] * kl : 0), RAVINE_RIM[2] + (rock ? rock[2] * kl : 0), nw * 0.8 * smooth(RAVINE_WALL_W * 0.92, RAVINE_WALL_W * 1.02, d) * (1 - smooth(RAVINE_WALL_W * 1.12, RAVINE_WALL_W * 1.3, d)));
  // a little dust round it all
  over(RAVINE_DUST[0], RAVINE_DUST[1], RAVINE_DUST[2], 0.2 * (1 - smooth(RAVINE_WALL_W, RAVINE_LIP_W * 1.3, d)));
  if (al <= 0) { out[0] = out[1] = out[2] = out[3] = 0; return out; }
  out[0] = r / al; out[1] = gg / al; out[2] = b / al; out[3] = al;
  return out;
}

/** The ravines as one painted gorge (Oct 14 2026; they were a lone two-stroke line per cell): a distance field from the
 *  joined segments, painted as a dark floor, shaded walls with the north-west rim lit, and a dusty lip, its width
 *  breathing along the way; the odd boulder sits on the edge. Drawn into a small bitmap stretched over the tile, like the
 *  lakes. */
function paintRavines(g: CanvasRenderingContext2D, x0: number, top: number, R: number, s: number, size: number, code: (x: number, y: number) => number, blocked: (x: number, y: number) => boolean, alpha: number, boulder: HTMLImageElement, sprites: { im: HTMLImageElement; x: number; y: number; w: number; h: number; a: number }[], mats?: TerrainMaterials, decorationBlocked?: (x: number, y: number, half: number) => boolean, pw?: PaintedRavine | null) {
  const rw = pw ? PW_RAVINE_W : RAVINE_W;
  const RAVINE_LIP_W = rw.lip;
  const isRavine = (x: number, y: number) => code(wrap(x, size), wrap(y, size)) === 4;
  const segs = ravineSegments(x0 - 3, top - R - 3, x0 + R + 2, top + 2, size, isRavine, blocked);
  if (!segs.length) return;
  const K = Math.max(4, Math.min(64, Math.round(s / 1.7)));
  const N = R * K;
  const D = new Float32Array(N * N).fill(9);
  const SX = new Float32Array(N * N); // the side of the nearest segment the sample is on (+ toward the light)
  const TP = new Float32Array(N * N).fill(1); // the taper at the nearest point: 1 along a gorge, down to 0 at a loose end
  const reach = RAVINE_LIP_W * 1.5;
  const lx = -0.6, ly = 0.8; // the light comes from the north-west (y north)
  for (const sg of segs) {
    const minx = Math.min(sg.ax, sg.bx) - reach, maxx = Math.max(sg.ax, sg.bx) + reach;
    const miny = Math.min(sg.ay, sg.by) - reach, maxy = Math.max(sg.ay, sg.by) + reach;
    const i0 = Math.max(0, Math.floor((minx - x0) * K)), i1 = Math.min(N - 1, Math.ceil((maxx - x0) * K));
    const j0 = Math.max(0, Math.floor((top - maxy) * K)), j1 = Math.min(N - 1, Math.ceil((top - miny) * K));
    if (i0 > i1 || j0 > j1) continue;
    const vx = sg.bx - sg.ax, vy = sg.by - sg.ay;
    const vv = vx * vx + vy * vy || 1e-6;
    for (let j = j0; j <= j1; j++) {
      const py = top - (j + 0.5) / K;
      for (let i = i0; i <= i1; i++) {
        const px = x0 + (i + 0.5) / K;
        const t = clamp(((px - sg.ax) * vx + (py - sg.ay) * vy) / vv, 0, 1);
        const qx = px - (sg.ax + vx * t), qy = py - (sg.ay + vy * t);
        const d = Math.hypot(qx, qy);
        const o = j * N + i;
        if (d < D[o]) {
          D[o] = d;
          SX[o] = d > 1e-6 ? (qx * lx + qy * ly) / d : 0;
          const tl = sg.t0 + (sg.t1 - sg.t0) * t;
          const fromEnd = Math.min(sg.freeA ? tl * sg.len : 9, sg.freeB ? (1 - tl) * sg.len : 9);
          TP[o] = 0.3 + 0.7 * smooth(0, Math.min(0.4, sg.len * 0.4), fromEnd);
        }
      }
    }
  }
  const c = document.createElement("canvas");
  c.width = c.height = N;
  const cg = c.getContext("2d");
  if (!cg) return;
  const img = cg.createImageData(N, N);
  const px = img.data;
  let any = false;
  const rockM = mats?.ravineRock ?? null;
  const floorM = mats?.ravineFloor ?? null;
  // painted world v2: the clip for the wall relief (alpha per sample)
  const MK = pw && pw.walls.some(Boolean) && s >= 24 ? new Uint8ClampedArray(N * N) : null;
  const tk = rockM || floorM ? ravineTextureStrength(s) : 0;
  for (let j = 0; j < N; j++) {
    const wy = top - (j + 0.5) / K;
    for (let i = 0; i < N; i++) {
      const o = j * N + i;
      if (D[o] > RAVINE_LIP_W * 1.4) continue;
      const wx = x0 + (i + 0.5) / K;
      // the width breathes along the gorge, and its edges are ragged
      const w = (0.62 + 0.6 * lakeWobble(wx, wy, size, 0.7, 1201)) * TP[o];
      // ragged, rocky edges
      const d = D[o] / w + (lakeNoise(wx, wy, size, 7, 7, 1207) - 0.5) * 0.04 + (lakeNoise(wx, wy, size, 19, 19, 1209) - 0.5) * 0.018;
      const grit = (lakeNoise(wx, wy, size, 11, 11, 1211) - 0.5) * 30; // rock texture on the walls
      // the light comes from the north-west: the gorge's north-west wall faces away from it (in shadow) and its lip catches
      // a bright edge; the south-east wall faces it and is lit
      const nw = smooth(-0.15, 0.45, SX[o]);
      if (pw) {
        // painted world v2: the painted rocks' own colours (sampled only where they show)
        const lit = d < rw.wall * 1.25 ? materialDelta(pw.lit, wx, wy, PRL) : PRL;
        const sh = d < rw.wall * 1.25 && nw > 0 ? materialDelta(pw.shadow, wx, wy, PRS) : PRS;
        const fl = d < rw.floor * 1.5 ? materialDelta(pw.floor, wx, wy, PRF) : PRF;
        const [r, gg, b, al] = paintedRavinePixel(d, nw, lit, sh, fl, rw, RV);
        if (al <= 0.004) continue;
        const q = o * 4;
        px[q] = r; px[q + 1] = gg; px[q + 2] = b; px[q + 3] = al * 255;
        // the wall relief's clip: inside the lip, on the gorge's northern half and its floor, fading across the floor
        if (MK) MK[o] = 255 * (1 - smooth(rw.lip * 0.88, rw.lip * 1.02, d)) * smooth(-0.55, -0.1, SX[o] * Math.min(1, d / rw.wall));
        any = true;
        continue;
      }
      // the painted rock and floor (Oct 14 2026), sampled in world cells: their variation over the lit / shaded colours
      const rock = tk > 0 && rockM && d < RAVINE_WALL_W * 1.35 ? materialDelta(rockM, wx, wy, RK) : null;
      const floor = tk > 0 && floorM && d < RAVINE_FLOOR_W * 1.6 ? materialDelta(floorM, wx, wy, FL) : null;
      const [r, gg, b, al] = ravinePixel(d, nw, grit, rock, floor, tk, RV);
      if (al <= 0.004) continue;
      const q = o * 4;
      px[q] = r;
      px[q + 1] = gg;
      px[q + 2] = b;
      px[q + 3] = al * 255;
      any = true;
    }
  }
  if (any) {
    cg.putImageData(img, 0, 0);
    g.save();
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = "medium";
    g.globalAlpha = alpha;
    g.drawImage(c, 0, 0, R * s, R * s);
    g.restore();
    if (MK && pw) paintWallRelief(g, segs, x0, top, R, s, size, alpha, pw, MK, N, rw, decorationBlocked);
  }
  c.width = c.height = 0;
  // a boulder on the lip of the odd ravine cell (with the trees, front to back)
  for (let y = top; y >= top - R - 1; y--) {
    for (let x = x0 - 1; x <= x0 + R; x++) {
      if (!isRavine(x, y)) continue;
      const wx = wrap(x, size), wy = wrap(y, size);
      if (cellHash(wx, wy, 320) > (pw ? 0.14 : 0.3)) continue;
      const [nx, ny] = ravineNode(x, y, size);
      const side = cellHash(wx, wy, 321) < 0.5 ? -1 : 1;
      const bx = nx + side * (pw ? 0.36 : 0.24), by = ny - (pw ? 0.3 : 0.18);
      const bw = s * (0.2 + 0.1 * cellHash(wx, wy, 322)) * SCENERY_SCALE;
      if (decorationBlocked?.(bx, by, bw / s / 2)) continue;
      const sx = (bx - x0) * s, sy = (top - by) * s;
      const rim = mats?.ravineRimRock;
      if (rim) {
        // the painted rubble (Oct 14 2026): its painted width = the boulder's, its contact point where the boulder stood
        // (never mirrored: it is lit from the north-west)
        const rr = accentRect(rim, bw, sx, sy);
        sprites.push({ im: rim.im, x: rr.x, y: rr.y, w: rr.w, h: rr.h, a: alpha });
        continue;
      }
      const ph = bw * (boulder.height / boulder.width);
      sprites.push({ im: boulder, x: sx - bw / 2, y: sy - ph * 0.86, w: bw, h: ph, a: alpha });
    }
  }
}

/** Painted world v2: the gorge-wall relief over a tile's painted gorges. Every authored wall patch shows its cliff face to
 *  the south, hanging below its rim, so it is the gorge's northern wall: on each joined link of a gorge (its middle piece)
 *  the patch whose measured rim runs closest to the link's own direction (within pickWall's tolerance — never rotated or
 *  mirrored, so the north-west light holds) is placed with its rim on the northern lip, about as long as the link. The
 *  patches are composed on their own layer and clipped to the gorge's band (its northern half and floor, `mask`), so the
 *  runtime geometry owns the joins, the ends and the clearances; directions no patch fits keep the painted rock beneath. */
function paintWallRelief(g: CanvasRenderingContext2D, segs: readonly RavineSeg[], x0: number, top: number, R: number, s: number, size: number, alpha: number, pw: PaintedRavine, mask: Uint8ClampedArray, N: number, rw: RavineWidths, blocked?: (x: number, y: number, half: number) => boolean) {
  const placed: { im: HTMLImageElement; x: number; y: number; w: number; h: number }[] = [];
  for (const sg of segs) {
    if (Math.abs(sg.t0 - 0.4) > 1e-6 || sg.len < 0.9) continue; // a link's middle piece; not a lone cell's stub
    const vx = sg.bx - sg.ax, vy = sg.by - sg.ay;
    const l = Math.hypot(vx, vy) || 1e-6;
    const ux = vx / l, uy = vy / l;
    const i = pickWall(imageAngle(ux, uy));
    if (i === null) continue;
    const im = pw.walls[i];
    if (!im) continue;
    const m = wallMeta(i);
    let nx = -uy, ny = ux;
    if (ny < 0) { nx = -nx; ny = -ny; }
    const mx = (sg.ax + sg.bx) / 2, my = (sg.ay + sg.by) / 2;
    const wf = 0.62 + 0.6 * lakeWobble(mx, my, size, 0.7, 1201);
    const lx = mx + nx * rw.lip * wf * 0.86, ly = my + ny * rw.lip * wf * 0.86;
    if (blocked?.(lx, ly, 0.2)) continue;
    const k = (Math.min(1.5, Math.max(0.8, sg.len * 1.15)) * s) / m.rimLength;
    const sx = (lx - x0) * s, sy = (top - ly) * s;
    placed.push({ im, x: sx - m.rimAnchor[0] * k, y: sy - m.rimAnchor[1] * k, w: m.w * k, h: m.h * k });
  }
  drawClipped(g, placed, mask, N, R, s, alpha);
}

/** Painted relief on its own layer, clipped by a per-sample alpha `mask` (N × N over the tile), then onto the tile. */
/** The part of the tile (CSS px) the placed relief covers, or null when none of it is on the tile. */
function reliefBounds(placed: readonly { x: number; y: number; w: number; h: number }[], R: number, s: number): { x0: number; y0: number; x1: number; y1: number } | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of placed) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x + p.w); y1 = Math.max(y1, p.y + p.h); }
  x0 = Math.max(0, Math.floor(x0)); y0 = Math.max(0, Math.floor(y0));
  x1 = Math.min(R * s, Math.ceil(x1)); y1 = Math.min(R * s, Math.ceil(y1));
  return x1 > x0 && y1 > y0 ? { x0, y0, x1, y1 } : null;
}

function drawClipped(g: CanvasRenderingContext2D, placed: { im: HTMLImageElement; x: number; y: number; w: number; h: number }[], mask: Uint8ClampedArray, N: number, R: number, s: number, alpha: number) {
  if (!placed.length) return;
  // the layer covers only the relief's own part of the tile (a whole-tile layer per tile costs more than the relief)
  const bb = reliefBounds(placed, R, s);
  if (!bb) return;
  const T = g.getTransform();
  const W = Math.max(1, Math.ceil((bb.x1 - bb.x0) * T.a)), H = Math.max(1, Math.ceil((bb.y1 - bb.y0) * T.d));
  const layer = document.createElement("canvas");
  const mc = document.createElement("canvas");
  try {
    layer.width = W;
    layer.height = H;
    const lg = layer.getContext("2d");
    mc.width = mc.height = N;
    const mg = mc.getContext("2d");
    if (!lg || !mg) return;
    const img = mg.createImageData(N, N);
    for (let i = 0; i < N * N; i++) img.data[i * 4 + 3] = mask[i];
    mg.putImageData(img, 0, 0);
    lg.setTransform(T.a, 0, 0, T.d, -bb.x0 * T.a, -bb.y0 * T.d);
    lg.imageSmoothingEnabled = true;
    lg.imageSmoothingQuality = "high";
    placed.sort((a, b) => a.y - b.y);
    for (const p of placed) lg.drawImage(p.im, p.x, p.y, p.w, p.h);
    lg.globalCompositeOperation = "destination-in";
    lg.imageSmoothingQuality = "medium";
    lg.drawImage(mc, 0, 0, R * s, R * s);
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalAlpha = alpha;
    g.drawImage(layer, T.e + bb.x0 * T.a, T.f + bb.y0 * T.d);
    g.restore();
  } finally {
    layer.width = layer.height = 0;
    mc.width = mc.height = 0;
  }
}
